import React, { useCallback, useState, useEffect, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { useBrandingColors } from '../hooks/useBrandingColors';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { 
  EyeIcon,
  EnvelopeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import AiFactPositive from '../assets/icons/ai-fact-positive.svg';
import AiFactNegative from '../assets/icons/ai-fact-negative.svg';
import EmailDialog from './EmailDialog';
import ContactsDialog from './ContactsDialog';
import CampaignCheckResultsModal from './CampaignCheckResultsModal';
import HHResumeModal from './components/HHResumeModal';
import EStaffResumeModal from './components/EStaffResumeModal';
import AvitoResumeModal from './components/AvitoResumeModal';
import { openHHResumeContacts, openAvitoResumeContacts, generateAICommentsBatch, checkCampaignCandidates } from './services/api';
import { checkCampaignCreationCredits, createCampaign } from '../services/aiRecruiterService';
import { useAuthStore } from '../store/authStore';
import { useVacancyStore } from '../store/vacancyStore';
import { apiService } from '../services/apiService';
import { useEstaffLinkGuard } from '../hooks/useEstaffLinkGuard';
import { getSourceMapper } from '../sources';
import { MAILINGS_NAV_STATE_KEY } from '../constants/clarifyExperience';
import { filterBlockedCandidates } from './utils/filterBlockedCandidates';
import { filterCandidatesForMailing } from './utils/mailingValidation';
import { getResultsLoadErrorMessage } from './utils/resultsErrorMessage';
import { getEstaffStatusClasses } from './utils/estaffStatusColor';
import { loadLatestCampaignReusableSettings } from './utils/campaignSettings';
import {
  CONTACTS_OPENED_LABEL,
  formatCandidateShortName,
  pickSafeHHResumeUrl,
} from './utils/candidatePrivacy';

// Extract Telegram from candidate
const extractTelegramFromCandidate = (candidate) => {
  if (candidate.telegram) return candidate.telegram;

  const telegramRegex = /@[a-zA-Z0-9_]{5,}/;
  const text = candidate.about || candidate.summary || '';
  const telegramMatch = text.match(telegramRegex);
  return telegramMatch ? telegramMatch[0] : null;
};

const normalizeCandidateText = (value) => {
  if (typeof value !== 'string') return '';
  return value.replace(/\s+/g, ' ').trim();
};

const normalizeOptionalId = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const hasRealHHNameParts = (row) => Boolean(
  normalizeCandidateText(row.firstName || row.first_name) ||
  normalizeCandidateText(row.lastName || row.last_name) ||
  normalizeCandidateText(row.middleName || row.middle_name)
);

const isTitleOnlyHHName = (row) => {
  const title = normalizeCandidateText(row.title || row.position);
  if (!title || hasRealHHNameParts(row)) return false;
  if (row.hiddenName) return true;

  const nameCandidates = [
    row.fullName,
    row.name,
    row.displayName,
  ].map(normalizeCandidateText).filter(Boolean);

  return nameCandidates.some((name) => name === title);
};

const isAvitoResumeIdLabel = (value) => /^Резюме\s*№/i.test(normalizeCandidateText(value));

const getAvitoCandidateDisplayName = (row, openedContacts = {}) => {
  const title = normalizeCandidateText(row.title || row.position);
  const candidate = {
    firstName: openedContacts.firstName || row.firstName || row.first_name,
    first_name: openedContacts.firstName || row.first_name,
    lastName: openedContacts.lastName || row.lastName || row.last_name,
    last_name: openedContacts.lastName || row.last_name,
    middleName: openedContacts.middleName || row.middleName || row.middle_name,
    middle_name: openedContacts.middleName || row.middle_name,
    fullName: openedContacts.fullName || openedContacts.name || row.fullName || row.full_name,
    name: openedContacts.name || openedContacts.fullName || row.name,
    displayName: openedContacts.displayName || row.displayName,
  };

  const hasNameParts = Boolean(
    normalizeCandidateText(candidate.firstName || candidate.first_name) ||
    normalizeCandidateText(candidate.lastName || candidate.last_name) ||
    normalizeCandidateText(candidate.middleName || candidate.middle_name)
  );
  const rawName = normalizeCandidateText(candidate.fullName || candidate.name || candidate.displayName);
  const hasRealFullName = Boolean(
    rawName &&
    !isAvitoResumeIdLabel(rawName) &&
    (!title || rawName !== title)
  );

  if (hasNameParts || hasRealFullName) {
    return formatCandidateShortName(candidate, row.displayName || row.name || row.fullName || 'Кандидат');
  }

  return row.fullName || row.name || row.displayName || '—';
};

const normalizePhoneDigits = (value) => normalizeCandidateText(value).replace(/[^0-9]/g, '');

const getTelegramLink = (value) => {
  const text = normalizeCandidateText(value);
  if (!text) return null;

  if (text.startsWith('@')) {
    const username = text.slice(1).trim();
    return username ? `https://t.me/${username}` : null;
  }

  const digits = normalizePhoneDigits(text);
  return digits ? `https://t.me/+${digits}` : null;
};

const getWhatsAppLink = (value) => {
  const digits = normalizePhoneDigits(value);
  return digits ? `https://wa.me/${digits}` : null;
};

const normalizeHHContactValue = (...values) => {
  for (const value of values) {
    if (!value) continue;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
    if (typeof value === 'object') {
      const phoneParts = value.country && value.city && value.number
        ? `${value.country}${value.city}${value.number}`
        : null;
      const raw = phoneParts || value.formatted || value.number || value.email || value.value || value.username || null;
      if (typeof raw === 'string' && raw.trim()) return raw.trim();
    }
  }
  return null;
};

const getHHContactTypeValues = (contact) => [
  contact?.type?.id,
  contact?.type?.name,
  contact?.kind,
].filter(Boolean).map(value => String(value).trim().toLowerCase());

const hasHHPreferredContact = (contact, normalizedTypeIds) => {
  const preferred = Array.isArray(contact?.preferred) ? contact.preferred : [];
  return preferred.some(item => normalizedTypeIds.includes(String(item?.id || '').trim().toLowerCase()));
};

const normalizeHHContactFields = (contact) => normalizeHHContactValue(
  contact?.contact_value,
  contact?.value,
  contact?.url,
  contact?.links?.web,
  contact?.links?.android,
  contact?.links?.ios
);

const getHHContactValue = (resume, typeIds) => {
  const contacts = Array.isArray(resume?.contact) ? resume.contact : [];
  const normalizedTypeIds = typeIds.map(typeId => String(typeId).trim().toLowerCase());
  const contact = contacts.find(item => {
    const contactTypes = getHHContactTypeValues(item);
    return normalizedTypeIds.some(typeId => contactTypes.includes(typeId));
  });
  const directValue = normalizeHHContactFields(contact);
  if (directValue) return directValue;

  const preferredContact = contacts.find(item => hasHHPreferredContact(item, normalizedTypeIds));
  return normalizeHHContactFields(preferredContact);
};

const getHHPhoneValue = (resume, campaignCandidate = {}) => {
  return campaignCandidate.phone || getHHContactValue(resume, ['cell', 'phone', 'home', 'work']) || null;
};

const pickHHMessengerLinks = ({ resume = {}, campaignCandidate = {}, phone, telegram, whatsapp }) => {
  const candidateLinks = campaignCandidate.messengerLinks || {};
  const telegramLink = telegram || candidateLinks.telegram || getHHContactValue(resume, ['telegram']) || null;
  const whatsappLink = whatsapp || candidateLinks.whatsapp || getHHContactValue(resume, ['whatsapp']) || phone || null;

  return {
    telegram: telegramLink,
    whatsapp: whatsappLink,
  };
};

const pickOpenedCampaignCandidate = (result = {}) => {
  const resume = result.resume || {};
  const campaignCandidate = result.campaignCandidate || {};
  const phone = getHHPhoneValue(resume, campaignCandidate);
  const telegram = campaignCandidate.telegram || resume.telegram || getHHContactValue(resume, ['telegram']) || null;
  const whatsapp = campaignCandidate.whatsapp || resume.whatsapp || getHHContactValue(resume, ['whatsapp']) || null;
  const messengerLinks = pickHHMessengerLinks({ resume, campaignCandidate, phone, telegram, whatsapp });

  return {
    email: campaignCandidate.email || getHHContactValue(resume, ['email']),
    phone,
    telegram,
    whatsapp,
    messengerLinks,
    photo: campaignCandidate.photoUrl || resume.photo?.medium || resume.photo?.small || null,
    resumeUrl: campaignCandidate.resumeUrl || resume.alternate_url || null,
    firstName: campaignCandidate.firstName || resume.first_name || null,
    lastName: campaignCandidate.lastName || resume.last_name || null,
    middleName: campaignCandidate.middleName || resume.middle_name || null,
    fullName: campaignCandidate.fullName || (resume.last_name && resume.first_name
      ? `${resume.last_name} ${resume.first_name}${resume.middle_name ? ' ' + resume.middle_name : ''}`
      : null),
  };
};

const pickOpenedAvitoCandidate = (result = {}) => {
  const resume = result.resume || {};
  const contacts = result.contacts || {};
  return {
    email: resume.email || contacts.email || null,
    phone: resume.phone || contacts.phone || null,
    avitoChatId: resume.avitoChatId || contacts.avitoChatId || null,
    firstName: resume.firstName || resume.first_name || null,
    lastName: resume.lastName || resume.last_name || null,
    middleName: resume.middleName || resume.middle_name || null,
    fullName: resume.fullName || resume.name || null,
    resumeUrl: resume.resumeUrl || resume.alternate_url || null,
  };
};

const getApiErrorMessage = (error) => {
  if (typeof error?.response?.data?.error === 'string') {
    return error.response.data.error;
  }
  return error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || '';
};

const getAvitoContactsOpenErrorMessage = (error) => {
  const message = getApiErrorMessage(error);
  if (error?.response?.status === 402) {
    return message || 'Недостаточно просмотров в пакете Авито';
  }
  if (error?.response?.status === 403) {
    return message || 'Нет доступа к контактам Авито. Проверьте права и подключение интеграции.';
  }
  return message ? `Ошибка Авито: ${message}` : 'Не удалось открыть контакты Авито';
};

// Трансформация кандидатов через SourceConfig.toPayload (единая точка маппинга)
const transformCandidatesForAPI = (candidates, source = 'hh') => {
  const mapper = getSourceMapper(source);
  return candidates.map(candidate => {
    const enriched = { ...candidate };
    if (source !== 'hh' && !enriched.telegram) {
      enriched.telegram = extractTelegramFromCandidate(candidate);
    }
    return mapper(enriched);
  });
};

const normalizeCandidateIdentityText = (value) => {
  if (!value) return null;
  const normalized = String(value)
    .normalize('NFKC')
    .replace(/[Aa]/g, 'а')
    .replace(/[Ee]/g, 'е')
    .replace(/[Oo]/g, 'о')
    .replace(/[Pp]/g, 'р')
    .replace(/[Cc]/g, 'с')
    .replace(/[Kk]/g, 'к')
    .replace(/[Xx]/g, 'х')
    .replace(/[Yy]/g, 'у')
    .replace(/\s+/g, ' ')
    .trim()
    .toLocaleLowerCase('ru-RU');
  return normalized || null;
};

const isHHCandidateForDedupe = (candidate) => {
  const source = String(candidate.source || candidate.candidateSource || '').trim().toLowerCase();
  return source.includes('hh') || Boolean(candidate.hhResumeId);
};

const getCandidateUniqueKey = (candidate) => {
  const hhResumeKey = candidate.hhResumeId || candidate.externalId || candidate.id;
  if (hhResumeKey && (isHHCandidateForDedupe(candidate) || isTitleOnlyHHName(candidate))) {
    return `hh:${hhResumeKey}`;
  }

  const nameKey = normalizeCandidateIdentityText(
    candidate.fullName
    || candidate.name
    || candidate.displayName
    || [candidate.lastName, candidate.firstName, candidate.middleName].filter(Boolean).join(' ')
  );
  return nameKey
    || candidate.hhResumeId
    || candidate.externalId
    || candidate.id
    || candidate.email
    || candidate.phone;
};

const dedupeCandidatesForCampaign = (candidates) => {
  const byKey = new Map();
  candidates.forEach(candidate => {
    const key = getCandidateUniqueKey(candidate);
    if (!key || byKey.has(key)) return;
    byKey.set(key, candidate);
  });
  return Array.from(byKey.values());
};

const sanitizeInheritedAccountSettings = (accountSettings, { allowHH }) => {
  if (!accountSettings || typeof accountSettings !== 'object') return accountSettings;
  const normalizeMessenger = (messenger) => String(messenger || '').toUpperCase();
  const isAllowed = (messenger) => allowHH || normalizeMessenger(messenger) !== 'HH';
  const enabledMessengers = Array.isArray(accountSettings.enabledMessengers)
    ? accountSettings.enabledMessengers.map(normalizeMessenger).filter(isAllowed)
    : [];
  const messengerPriority = Array.isArray(accountSettings.messengerPriority)
    ? accountSettings.messengerPriority.map(normalizeMessenger).filter(isAllowed)
    : enabledMessengers;

  return {
    ...accountSettings,
    enabledMessengers: Array.from(new Set(enabledMessengers)),
    messengerPriority: Array.from(new Set(messengerPriority))
  };
};

const getOpenContactsFailureReason = (error) => {
  const status = error?.response?.status;
  if (status === 402) return 'недостаточно средств для открытия контактов';
  if (status === 403) return 'нет доступа к открытию контактов';
  if (status === 404) return 'резюме не найдено';
  if (status === 429) return 'превышен лимит HH';

  const responseMessage = error?.response?.data?.message || error?.response?.data?.error;
  if (responseMessage) return `не удалось открыть контакты: ${responseMessage}`;

  return 'не удалось открыть контакты';
};

const summarizeRejectionReasons = (rejections) => {
  const byReason = new Map();
  rejections.forEach(({ reason }) => {
    byReason.set(reason, (byReason.get(reason) || 0) + 1);
  });

  return Array.from(byReason.entries())
    .map(([reason, count]) => `${reason} — ${count}`)
    .join('; ');
};

const getCandidateModalName = (candidate) => {
  return candidate.fullName
    || candidate.name
    || candidate.displayName
    || [candidate.lastName, candidate.firstName, candidate.middleName].filter(Boolean).join(' ')
    || candidate.title
    || candidate.position
    || 'Неизвестный кандидат';
};

const ResultsTable = ({ data, loading, loadingMore, error, filters, onPageChange, onExpandedCommentChange, sourcePositionId, vacancyTitle, selectedRelevance, searchProgress, onCancelSearch, onAiScoresChange }) => {
  const navigate = useNavigate();
  const colors = useBrandingColors();
  const { selectedVacancyId, selectedVacancy } = useVacancyStore();
  // TASKNEIROKLYUCH-435 FR-1: guard-хук для проверки привязки к E-Staff вакансии
  const { ensureEstaffLink, checkEstaffLinkStatus, guardModal: estaffLinkGuardModal } = useEstaffLinkGuard();

  // Безопасная функция для рендеринга данных
  const getSafeText = (value, fallback = '—') => {
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      return value.name || value.text || value.primary || value.title || value.organization || value.result || fallback;
    }
    return fallback;
  };

  const getStatusDateValue = (row) => {
    if (row.statusDate || row.currentStatus?.date) {
      return row.statusDate || row.currentStatus.date;
    }

    if (!Array.isArray(row.statusHistory) || row.statusHistory.length === 0) return null;
    const currentStatusId = row.currentStatus?.id;
    const sortedStatusHistory = row.statusHistory
      .filter(item => item?.date)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const currentStatusHistoryItem = currentStatusId
      ? sortedStatusHistory.find(item => item?.status?.id === currentStatusId)
      : null;
    return currentStatusHistoryItem?.date || sortedStatusHistory[0]?.date || null;
  };

  const formatStatusDate = (value) => {
    if (!value) return '—';
    const text = String(value).trim();
    const dateOnlyMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const date = dateOnlyMatch
      ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
      : new Date(text);
    if (Number.isNaN(date.getTime())) return String(value);
    return format(date, 'dd.MM.yyyy', { locale: ru });
  };

  const getSelectionStorageKey = (id) => id ? `selectedCandidates_v${id}` : 'selectedCandidates';
  const getStorage = (id) => id ? localStorage : sessionStorage;
  const getResultsCacheKey = (id) => id ? `searchResultsCache_v${id}` : 'searchResultsCache';

  // Helper to update a single item in the search results cache
  const updateSearchResultsCache = (itemId, updates) => {
    try {
      const storage = getStorage(selectedVacancyId);
      const cacheKey = getResultsCacheKey(selectedVacancyId);
      const cached = storage.getItem(cacheKey);
      if (!cached) return;

      const cacheData = JSON.parse(cached);
      if (!cacheData.results?.items) return;

      cacheData.results.items = cacheData.results.items.map(item =>
        item.id === itemId ? { ...item, ...updates } : item
      );

      storage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (e) {
      console.error('Error updating search results cache:', e);
    }
  };

  const loadState = (id) => {
    try {
      const storage = getStorage(id);
      const key = getSelectionStorageKey(id);
      const saved = storage.getItem(key);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading selection:', e);
    }
    return { selected: [], map: {} };
  };

  const [selected, setSelected] = useState(() => loadState(selectedVacancyId).selected);
  const [selectedCandidatesMap, setSelectedCandidatesMap] = useState(() => loadState(selectedVacancyId).map);
  const [lastVacancyId, setLastVacancyId] = useState(selectedVacancyId);

  // Save changes
  useEffect(() => {
    const storage = getStorage(selectedVacancyId);
    const key = getSelectionStorageKey(selectedVacancyId);
    storage.setItem(key, JSON.stringify({
      selected,
      map: selectedCandidatesMap
    }));
  }, [selected, selectedCandidatesMap, selectedVacancyId]);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState({});
  const [contactsData, setContactsData] = useState({});
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [estaffLinkMissing, setEstaffLinkMissing] = useState(false);
  const [checkingContactEstaffLink, setCheckingContactEstaffLink] = useState(false);
  const [contactEstaffVacancyId, setContactEstaffVacancyId] = useState('');
  const [selectedResumeId, setSelectedResumeId] = useState(null);
  const [selectedResumeData, setSelectedResumeData] = useState(null);
  
  // State for HH resume modal
  const [hhResumeModalOpen, setHhResumeModalOpen] = useState(false);
  const [selectedHHResume, setSelectedHHResume] = useState(null);

  // State for E-Staff resume modal
  const [estaffResumeModalOpen, setEstaffResumeModalOpen] = useState(false);
  const [selectedEStaffResume, setSelectedEStaffResume] = useState(null);

  // State for Avito resume modal (TASKNEIROKLYUCH-765)
  const [avitoResumeModalOpen, setAvitoResumeModalOpen] = useState(false);
  const [selectedAvitoResume, setSelectedAvitoResume] = useState(null);

  // State for campaign creation modal
  const [creatingCampaign, setCreatingCampaign] = useState(false);

  // Модалка результатов проверки кандидатов (TASKNEIROKLYUCH-434)
  const [checkResultsModalOpen, setCheckResultsModalOpen] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [pendingCampaignData, setPendingCampaignData] = useState(null);

  const estaffMissingWarning = 'Для этой позиции не выбрана вакансия E-Staff. Контакты будут открыты в HH, но кандидаты не будут сохранены в E-Staff.';

  // State for clicked row highlighting
  const [clickedRowId, setClickedRowId] = useState(null);

  // TASKNEIROKLYUCH-645: клиентская пагинация для relevance-режима.
  // Должна быть здесь (наверху), чтобы Rules of Hooks не ломались —
  // ниже есть условные early-return'ы.
  const [clientPage, setClientPage] = useState(0);
  const pendingClientPageRef = useRef(null);
  // Сброс клиентской страницы при новом поиске (смене data).
  // В обычном режиме сброс не нужен — там используется serverPage.
  useEffect(() => {
    if (pendingClientPageRef.current !== null) {
      const targetPage = pendingClientPageRef.current;
      pendingClientPageRef.current = null;
      setClientPage(targetPage);
      return;
    }
    setClientPage(0);
  }, [data]);

  const [aiComments, setAiComments] = useState({});
  const [aiRatings, setAiRatings] = useState({});
  const [aiMatchMeta, setAiMatchMeta] = useState({});
  const [aiRowOverrides, setAiRowOverrides] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  // AI-комментарий показывается в popover'е, а не раскрывается внутри ячейки —
  // раскрытие внутри таблицы распирало строку на произвольную высоту.
  const [aiPopover, setAiPopover] = useState(null);
  const [queuedComments, setQueuedComments] = useState({});

  const [commentQueue, setCommentQueue] = useState([]);

  const [scoringStats, setScoringStats] = useState({ total: 0, fromCache: 0, newlyScored: 0 });

  // Handle vacancy change (derived state pattern). Must run AFTER all per-vacancy
  // state is declared, since setters used here are defined above.
  // Scoring state (aiComments/aiRatings/...) is keyed by candidate ID, not by
  // vacancyId — without a reset, a candidate returned by both vacancies shows
  // the previous vacancy's comment/score because the "already have a comment"
  // check in the queueing effect skips re-scoring.
  if (lastVacancyId !== selectedVacancyId) {
    const newState = loadState(selectedVacancyId);
    setSelected(newState.selected);
    setSelectedCandidatesMap(newState.map);
    setAiComments({});
    setAiRatings({});
    setAiMatchMeta({});
    setAiRowOverrides({});
    setLoadingComments({});
    setAiPopover(null);
    setQueuedComments({});
    setCommentQueue([]);
    setScoringStats({ total: 0, fromCache: 0, newlyScored: 0 });
    setLastVacancyId(selectedVacancyId);
  }

  const aiCommentsRef = useRef(aiComments);
  aiCommentsRef.current = aiComments;
  const aiRowOverridesRef = useRef(aiRowOverrides);
  aiRowOverridesRef.current = aiRowOverrides;
  const loadingCommentsRef = useRef(loadingComments);
  loadingCommentsRef.current = loadingComments;

  const prevFiltersRef = useRef();
  const scoringEnabled = filters?.scoringEnabled !== false;

  const getStorageKey = useCallback(() => {
    const source = filters.source || 'estaff';
    const searchKey = JSON.stringify({
      source,
      query: filters.query || '',
      page: filters.page || 1
    });
    return `clickedRow_${btoa(searchKey)}`;
  }, [filters.source, filters.query, filters.page]);

  // Get rating label based on relevance categories
  const getRatingLabel = (rating) => {
    if (typeof rating !== 'number') return '';
    if (rating >= 75) return 'Высокое соответствие';
    if (rating >= 50) return 'Среднее соответствие';
    return 'Низкое соответствие';
  };

  // Get the best available score (prefer server-side aiRating, then client-side)
  const getDisplayScore = (rowId, itemData = null) => {
    // 1. First check if item has server-side aiRating (from search-with-relevance endpoint)
    if (itemData?.aiRating !== undefined && itemData.aiRating > 0) {
      return { score: itemData.aiRating, source: 'server' };
    }
    // 2. If we have client-side AI rating (from expandable comments), use it
    if (aiRatings[rowId] !== undefined && aiRatings[rowId] > 0) {
      return { score: aiRatings[rowId], source: 'ai' };
    }
    return null;
  };
  
  
  useEffect(() => {
    const storageKey = getStorageKey();
    const savedClickedRow = localStorage.getItem(storageKey);
    if (savedClickedRow) {
      setClickedRowId(savedClickedRow);
    } else {
      setClickedRowId(null);
    }
  }, [getStorageKey]);
  
  useEffect(() => {
    if (clickedRowId && data?.items) {
      const rowExists = data.items.some(item => item.id === clickedRowId);
      if (!rowExists) {
        setClickedRowId(null);
      }
    }
  }, [data?.items, clickedRowId]);

  useEffect(() => {
    const filtersHaveChanged = (() => {
      if (!prevFiltersRef.current) {
        return false;
      }
      const prev = { ...prevFiltersRef.current };
      delete prev.page;
      delete prev.pageSize;
      delete prev.limit;

      const current = { ...filters };
      delete current.page;
      delete current.pageSize;
      delete current.limit;

      const stableStringify = (obj) => JSON.stringify(Object.keys(obj).sort().reduce((acc, key) => ({ ...acc, [key]: obj[key] }), {}));
      return stableStringify(prev) !== stableStringify(current);
    })();

    prevFiltersRef.current = filters;

    const clearAllAiState = () => {
      setAiComments(prev => Object.keys(prev).length > 0 ? {} : prev);
      setAiRatings(prev => Object.keys(prev).length > 0 ? {} : prev);
      setAiMatchMeta(prev => Object.keys(prev).length > 0 ? {} : prev);
      setAiRowOverrides(prev => Object.keys(prev).length > 0 ? {} : prev);
      setLoadingComments(prev => Object.keys(prev).length > 0 ? {} : prev);
      setQueuedComments(prev => Object.keys(prev).length > 0 ? {} : prev);
      setCommentQueue(prev => prev.length > 0 ? [] : prev);
    };

    if (loading) {
      if (filtersHaveChanged) {
        setAiComments(prev => Object.keys(prev).length > 0 ? {} : prev);
        setAiRatings(prev => Object.keys(prev).length > 0 ? {} : prev);
        setAiMatchMeta(prev => Object.keys(prev).length > 0 ? {} : prev);
        setAiRowOverrides(prev => Object.keys(prev).length > 0 ? {} : prev);
        setScoringStats({ total: 0, fromCache: 0, newlyScored: 0 });
      }
      setCommentQueue(prev => prev.length > 0 ? [] : prev);
      setLoadingComments(prev => Object.keys(prev).length > 0 ? {} : prev);
      setQueuedComments(prev => Object.keys(prev).length > 0 ? {} : prev);
      return;
    }

    if (!scoringEnabled) {
      clearAllAiState();
      return;
    }

    if (data?.scoringApplied === true) {
      const items = data.items || [];
      const hasAiData = items.some(i => i.aiComment || (i.aiRating && i.aiRating > 0));
      if (hasAiData) {
        clearAllAiState();
        const cached = items.filter(i => i.fromCache).length;
        setScoringStats({ total: items.length, fromCache: cached, newlyScored: items.length - cached });
        return;
      }
    }

    if (data?.items && data.items.length > 0) {
      const firstItem = data.items[0];
      const hasSearchCriteria = firstItem && (firstItem.matchComment || firstItem.matchScore > 0);
      const isRestoredFromCache = data?.scoringApplied === true && !data.items.some(i => i.aiComment || (i.aiRating && i.aiRating > 0));

      if (hasSearchCriteria || isRestoredFromCache) {
        const currentAiComments = aiCommentsRef.current;
        const currentAiRowOverrides = aiRowOverridesRef.current;
        const itemsToQueue = [];
        const initialQueuedState = {};

        data.items.forEach(item => {
          if (currentAiComments[item.id] === undefined && !currentAiRowOverrides[item.id]?.scoringError) {
            itemsToQueue.push(item);
            initialQueuedState[item.id] = true;
          }
        });

        setQueuedComments(initialQueuedState);
        setCommentQueue(itemsToQueue);

      } else {
        setLoadingComments(prev => Object.keys(prev).length > 0 ? {} : prev);
        setQueuedComments(prev => Object.keys(prev).length > 0 ? {} : prev);
        setCommentQueue(prev => prev.length > 0 ? [] : prev);
      }
    }
  }, [data?.items, data?.scoringApplied, loading, filters, scoringEnabled]);

  useEffect(() => {
    const loadBatch = async () => {
      if (commentQueue.length === 0) {
        return;
      }
      const currentAiComments = aiCommentsRef.current;
      const currentAiRowOverrides = aiRowOverridesRef.current;
      const currentLoadingComments = loadingCommentsRef.current;
      const itemsToLoad = commentQueue.filter(item => {
        const isLoading = currentLoadingComments[item.id];
        const hasResolvedResult = currentAiComments[item.id] !== undefined || !!currentAiRowOverrides[item.id]?.scoringError;
        return !hasResolvedResult && !isLoading;
      }).slice(0, 8);
      if (itemsToLoad.length === 0) {
        return;
      }
      const ids = itemsToLoad.map(item => item.id);
      const loadingState = {};
      ids.forEach(id => {
        loadingState[id] = true;
      });
      setLoadingComments(prev => ({ ...prev, ...loadingState }));
      try {
        // Inject position's saved weighted requirements (not in the user-facing filters state).
        // Without this, the scorer can't evaluate candidates against "Знание Langchain" etc.
        const filtersForScoring = selectedVacancy?.aiRequirementsWeighted
          ? { ...filters, aiRequirementsWeighted: selectedVacancy.aiRequirementsWeighted }
          : filters;
        const responses = await Promise.allSettled(
          itemsToLoad.map(item => generateAICommentsBatch([item], filtersForScoring))
        );
        const items = {};
        const requestOverrides = {};
        responses.forEach((entry, index) => {
          const item = itemsToLoad[index];
          const id = item.id;
          if (entry.status === 'rejected') {
            const error = entry.reason;
            requestOverrides[id] = {
              scoringError: 'SCORING_UNAVAILABLE',
              scoringErrorMessage: error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Ошибка загрузки'
            };
            return;
          }
          const data = entry.value?.items?.[id] || entry.value?.items?.[String(id)];
          if (data) {
            items[id] = data;
          }
        });
        const newComments = {};
        const newRatings = {};
        const newMeta = {};
        const newOverrides = { ...requestOverrides };
        let batchFromCache = 0;
        let batchNewlyScored = 0;
        Object.keys(items).forEach(id => {
          const data = items[id];
          if (!data) {
            return;
          }
          // Сохраняем ID как строку — большие числа E-Staff (19 цифр) теряют точность при Number()
          const numericId = id;
          if (data.scoringError || data.error) {
            newOverrides[numericId] = {
              scoringError: data.scoringError || 'SCORING_UNAVAILABLE',
              scoringErrorMessage: data.scoringErrorMessage || data.error || 'Оценка резюме временно недоступна'
            };
            return;
          }
          newComments[numericId] = data.comment ?? null;
          newRatings[numericId] = typeof data.rating === 'number' ? data.rating : 0;
          newMeta[numericId] = {
            rating: typeof data.rating === 'number' ? data.rating : 0,
            matchLevel: data.matchLevel || null,
            matchLabel: data.matchLabel || null
          };
          if (data.fromCache) {
            batchFromCache++;
          } else {
            batchNewlyScored++;
          }
        });
        if (Object.keys(newRatings).length > 0) {
          setAiComments(prev => ({ ...prev, ...newComments }));
          setAiRatings(prev => ({ ...prev, ...newRatings }));
          setAiMatchMeta(prev => ({ ...prev, ...newMeta }));
          setAiRowOverrides(prev => {
            const next = { ...prev };
            Object.keys(newRatings).forEach(id => {
              delete next[id];
            });
            return next;
          });
          setScoringStats(prev => ({
            total: prev.total + Object.keys(newRatings).length,
            fromCache: prev.fromCache + batchFromCache,
            newlyScored: prev.newlyScored + batchNewlyScored
          }));
          if (typeof onAiScoresChange === 'function') {
            const scoredItems = itemsToLoad
              .map(item => {
                const id = String(item.id);
                if (newRatings[id] === undefined) return null;
                return {
                  ...item,
                  aiRating: newRatings[id],
                  aiComment: newComments[id] ?? null,
                  fromCache: !!items[id]?.fromCache,
                  matchLevel: newMeta[id]?.matchLevel || null
                };
              })
              .filter(Boolean);
            if (scoredItems.length > 0) {
              onAiScoresChange(scoredItems);
            }
          }
        }
        if (Object.keys(newOverrides).length > 0) {
          setAiRowOverrides(prev => ({ ...prev, ...newOverrides }));
        }
      } catch (error) {
        const fallbackMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Ошибка загрузки';
        const batchOverrides = {};
        itemsToLoad.forEach(item => {
          batchOverrides[item.id] = {
            scoringError: 'SCORING_UNAVAILABLE',
            scoringErrorMessage: fallbackMessage
          };
        });
        setAiRowOverrides(prev => ({ ...prev, ...batchOverrides }));
      } finally {
        const cleared = {};
        itemsToLoad.forEach(item => {
          cleared[item.id] = false;
        });
        setLoadingComments(prev => {
          const next = { ...prev };
          Object.keys(cleared).forEach(id => {
            delete next[id];
          });
          return next;
        });
        setQueuedComments(prev => {
          const next = { ...prev };
          itemsToLoad.forEach(item => {
            delete next[item.id];
          });
          return next;
        });
        setCommentQueue(prev => prev.filter(item => !itemsToLoad.some(loaded => loaded.id === item.id)));
      }
    };
    loadBatch();
  }, [commentQueue, filters, selectedVacancy?.aiRequirementsWeighted, onAiScoresChange]);

  // Save clicked row to localStorage
  const saveClickedRow = (rowId) => {
    const storageKey = getStorageKey();
    if (rowId) {
      localStorage.setItem(storageKey, rowId);
    } else {
      localStorage.removeItem(storageKey);
    }
    setClickedRowId(rowId);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const pageItems = data?.items || [];
      setSelected(prev => {
        const existing = new Set(prev);
        pageItems.forEach(item => existing.add(item.id));
        return Array.from(existing);
      });
      setSelectedCandidatesMap(prev => {
        const next = { ...prev };
        pageItems.forEach(item => {
          next[item.id] = item;
        });
        return next;
      });
    } else {
      const pageIds = new Set((data?.items || []).map(item => item.id));
      setSelected(prev => prev.filter(id => !pageIds.has(id)));
      setSelectedCandidatesMap(prev => {
        const next = { ...prev };
        pageIds.forEach(id => {
          delete next[id];
        });
        return next;
      });
    }
  };

  const handleSelect = (row) => {
    const id = row.id;
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
    setSelectedCandidatesMap(prev => {
      const next = { ...prev };
      if (selectedIndex === -1) {
        next[id] = row;
      } else {
        delete next[id];
      }
      return next;
    });
  };


  // TASKNEIROKLYUCH-673: маппинг статусов E-Staff вынесен в utils/estaffStatusColor.
  // Берём цвет по state_id (стабильный код), не по русскому имени —
  // имена в справочнике E-Staff меняются, ID — нет.

  const formatSalary = (salary) => {
    if (!salary || !salary.amount) return '—';
    return `${salary.amount.toLocaleString()} ${salary.currency}`;
  };

  const handleShowContacts = (row) => {
    setSelectedResumeId(row.id);
    setSelectedResumeData(row);
    setContactEstaffVacancyId('');
    setEstaffLinkMissing(false);
    setContactsDialogOpen(true);
    if (filters.source === 'avito') {
      return;
    }
    if (selectedVacancyId) {
      setCheckingContactEstaffLink(true);
      (async () => {
        try {
          const estaffLink = await checkEstaffLinkStatus(selectedVacancyId);
          const id = estaffLink.estaffVacancyId;
          setEstaffLinkMissing(Boolean(estaffLink.estaffEnabled && !id));
          setContactEstaffVacancyId(id || '');
        } catch (err) {
          console.warn('Не удалось проверить привязку E-Staff source-link', err);
          setEstaffLinkMissing(false);
          setContactEstaffVacancyId('');
        } finally {
          setCheckingContactEstaffLink(false);
        }
      })();
    }
  };

  const handleShowHHResume = (row) => {
    setSelectedHHResume(row);
    setHhResumeModalOpen(true);
  };

  const handleShowEStaffResume = (row) => {
    setSelectedEStaffResume(row);
    setEstaffResumeModalOpen(true);
  };

  const handleShowAvitoResume = (row) => {
    setSelectedAvitoResume(row);
    setAvitoResumeModalOpen(true);
  };

  const handleConfirmShowContacts = async () => {
    if (!selectedResumeId || !selectedResumeData) return;

    const resumeId = selectedResumeId;
    let openedContacts = false;
    const isAvitoContact = filters.source === 'avito' || String(selectedResumeData.source || '').toLowerCase() === 'avito';

    try {
      if (isAvitoContact) {
        setLoadingContacts(prev => ({ ...prev, [resumeId]: true }));
        const result = await openAvitoResumeContacts(resumeId);
        openedContacts = true;
        const openedCandidate = pickOpenedAvitoCandidate(result);

        if (!openedCandidate.avitoChatId) {
          toast.error('Авито открыл контакты, но chat_id не пришёл. Отправка сообщения недоступна.');
          return;
        }

        const updates = {
          contactsOpened: true,
          contactsDisplay: CONTACTS_OPENED_LABEL,
          canViewContacts: true,
          firstName: openedCandidate.firstName,
          lastName: openedCandidate.lastName,
          middleName: openedCandidate.middleName,
          name: openedCandidate.fullName,
          fullName: openedCandidate.fullName,
          email: openedCandidate.email,
          phone: openedCandidate.phone,
          avitoChatId: openedCandidate.avitoChatId,
          resumeUrl: openedCandidate.resumeUrl || selectedResumeData.resumeUrl,
        };

        setContactsData(prev => ({
          ...prev,
          [resumeId]: updates
        }));
        setSelectedCandidatesMap(prev => prev[resumeId]
          ? { ...prev, [resumeId]: { ...prev[resumeId], ...updates } }
          : prev);
        updateSearchResultsCache(resumeId, updates);

        toast.success('Контакты Авито успешно открыты');
        setContactsDialogOpen(false);
        return;
      }

      // vacancyId для E-Staff sync — это external ID вакансии в E-Staff.
      // Если source-link не настроен, HH-контакты всё равно открываются,
      // а backend пропускает постановку E-Staff job.
      const estaffVacancyId = contactEstaffVacancyId.trim();

      setLoadingContacts(prev => ({ ...prev, [resumeId]: true }));
      const result = await openHHResumeContacts(resumeId, {
        canViewContacts: selectedResumeData.canViewContacts,
        vacancyId: estaffVacancyId || undefined,
      });
      openedContacts = true;
      const resume = result.resume;
      const openedCandidate = pickOpenedCampaignCandidate(result);

      setContactsData(prev => ({
        ...prev,
        [resumeId]: {
          contactsOpened: true,
          contactsDisplay: CONTACTS_OPENED_LABEL,
          firstName: openedCandidate.firstName || resume.first_name || null,
          lastName: openedCandidate.lastName || resume.last_name || null,
          middleName: openedCandidate.middleName || resume.middle_name || null,
          name: openedCandidate.fullName || (resume.last_name && resume.first_name
            ? `${resume.last_name} ${resume.first_name}${resume.middle_name ? ' ' + resume.middle_name : ''}`
            : null),
          telegram: openedCandidate.telegram,
          whatsapp: openedCandidate.whatsapp,
          messengerLinks: openedCandidate.messengerLinks,
        }
      }));

      // Update search results cache so contacts persist after page reload
      updateSearchResultsCache(resumeId, {
        canViewContacts: true,
        contactsOpened: true,
        contactsDisplay: CONTACTS_OPENED_LABEL,
        telegram: openedCandidate.telegram,
        whatsapp: openedCandidate.whatsapp,
        messengerLinks: openedCandidate.messengerLinks,
      });

      toast.success('Контакты успешно открыты');
      setContactsDialogOpen(false);
    } catch (error) {
      console.error('Error opening contacts:', error);

      const errorMessage = getApiErrorMessage(error);

      if (isAvitoContact) {
        toast.error(getAvitoContactsOpenErrorMessage(error));
      } else if (error.response?.status === 403) {
        toast.error('Недостаточно прав доступа. Проверьте вашу подписку HH.ru');
      } else if (errorMessage.includes('Недостаточно прав')) {
        toast.error(
          'У вашего аккаунта нет доступа к контактам. ' +
          'Необходима подписка на базу резюме HH.ru'
        );
      } else if (error.response?.status === 404) {
        toast.error('Резюме не найдено или скрыто кандидатом');
      } else {
        toast.error('Ошибка: ' + errorMessage);
      }
    } finally {
      setLoadingContacts(prev => ({ ...prev, [resumeId]: false }));
      if (openedContacts) {
        setSelectedResumeId(null);
        setSelectedResumeData(null);
      }
    }
  };

  const createPreparedCampaign = async (campaignData) => {
    setCreatingCampaign(true);
    try {
      const companyId = useAuthStore.getState().user?.companyId;
      const createdCampaign = await createCampaign(campaignData, {
        companyId
      });

      setSelected([]);
      setSelectedCandidatesMap({});

      if (createdCampaign?.id) {
        navigate('/recruiter/mailings', {
          state: {
            [MAILINGS_NAV_STATE_KEY]: {
              campaignId: String(createdCampaign.id),
              activeTab: 'settings',
            },
          },
        });
      } else {
        navigate('/recruiter/mailings');
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      console.error('Error response:', error.response);
      console.error('Error request:', error.config);

      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);
      }

      const errorMessage = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : error.response?.data?.error?.message || error.response?.data?.message || error.message;

      toast.error('Ошибка создания рассылки: ' + errorMessage);
    } finally {
      setCreatingCampaign(false);
    }
  };

  const handleCreateCampaign = async ({ skipCandidateCheck = false, filteredCandidates = null } = {}) => {
    setCreatingCampaign(true);
    try {
      // TASKNEIROKLYUCH-434: явный пустой массив = все кандидаты отфильтрованы
      // при предыдущей проверке. Не откатываемся на validCandidates на line 1079.
      if (Array.isArray(filteredCandidates) && filteredCandidates.length === 0) {
        toast.error('Нет доступных кандидатов для рассылки');
        return;
      }

      // Get selected candidates data from map (includes candidates from all pages).
      // Мерджим AI-оценку из клиентских стейтов (aiRatings/aiComments/aiMatchMeta) —
      // когда скор пришёл не в response поиска, а был посчитан на клике "сгенерировать",
      // он лежит в отдельных стейтах и не попадает в row → теряется при сохранении в selectedCandidatesMap.
      const selectedCandidates = selected
        .map(id => selectedCandidatesMap[id])
        .filter(candidate => !!candidate)
        .map(cand => {
          const meta = aiMatchMeta[cand.id];
          return {
            ...cand,
            aiRating: cand.aiRating ?? aiRatings[cand.id] ?? meta?.rating,
            aiComment: cand.aiComment ?? aiComments[cand.id],
            matchLevel: cand.matchLevel ?? meta?.matchLevel,
          };
        });
      const uniqueSelectedCandidates = dedupeCandidatesForCampaign(selectedCandidates);

      if (uniqueSelectedCandidates.length === 0) {
        toast.error('Нет выбранных кандидатов');
        return;
      }

      // Check if we have a selected vacancy
      if (!selectedVacancyId) {
        toast.error('Не выбрана вакансия');
        return;
      }

      const companyId = useAuthStore.getState().user?.companyId;
      const aiScreeningEnabled = Boolean(selectedVacancy?.aiScreeningEnabled);
      let inheritedCampaignSettings = {};
      try {
        inheritedCampaignSettings = await loadLatestCampaignReusableSettings(selectedVacancyId);
      } catch (error) {
        console.warn('Failed to load previous campaign settings, using defaults', error);
      }

      if (companyId) {
        const creditsCheck = await checkCampaignCreationCredits(
          companyId,
          uniqueSelectedCandidates.length,
          aiScreeningEnabled,
          selectedVacancyId
        );

        if (creditsCheck.status === 'ERROR') {
          toast.error(
            creditsCheck.message ||
            `Количество кандидатов превышает количество доступных диалогов. Вы можете запустить ${creditsCheck.maxLaunchable} диалогов. Запросите расширение доступа у администратора.`
          );
          return;
        }

        if (creditsCheck.status === 'WARNING') {
          toast(
            creditsCheck.message ||
            `Всего доступно ${creditsCheck.totalAvailable} диалогов, для WhatsApp/Telegram/Max ${creditsCheck.whatsappAvailable}. Рассылка будет создана, но запуск может быть недоступен для части каналов.`,
            {
              icon: '⚠️',
              duration: 8000
            }
          );
        }
      }

      // Always load full vacancy data from API to ensure all fields are present
      let fullVacancyData;
      try {
        const response = await apiService.getPosition(selectedVacancyId);
        // apiService returns data directly, not in response.data
        fullVacancyData = response.data || response;
      } catch (error) {
        console.error('Error loading vacancy data:', error);
        toast.error('Не удалось загрузить данные вакансии');
        return;
      }

      // Open HH contacts for all selected candidates (for HH source)
      const isHH = filters.source === 'hh';
      const isAvito = filters.source === 'avito';
      const candidatesWithContacts = [];
      const rejectedCandidates = [];
      let campaignEstaffVacancyId;
      let campaignHhVacancyId;
      let campaignAvitoVacancyId;

      if (isHH) {
        campaignHhVacancyId = fullVacancyData?.hhVacancyId
          || selectedVacancy?.hhVacancyId
          || await apiService.resolveSourceVacancyId(selectedVacancyId, 'HH');
        // Резолвим external E-Staff vacancy ID один раз перед открытием контактов.
        // Если E-Staff включён и метки нет, предупреждение остаётся в существующей
        // модалке создания рассылки, отдельную E-Staff модалку не открываем.
        // Если интеграция выключена — не шумим предупреждением и не пытаемся sync.
        const estaffLink = await checkEstaffLinkStatus(selectedVacancyId);
        campaignEstaffVacancyId = estaffLink.estaffVacancyId;
        if (estaffLink.estaffEnabled && !campaignEstaffVacancyId) {
          toast(estaffMissingWarning, { icon: '⚠️', duration: 8000 });
        }

        toast.loading('Открываем контакты кандидатов...', { id: 'opening-contacts' });

        const debugFailSetting = window.localStorage?.getItem('rel250DebugFailLastCandidate');
        const debugFailLastCandidate = process.env.NODE_ENV !== 'test'
          && (
            debugFailSetting === '1'
            || (debugFailSetting !== '0' && window.location.hostname === 'localhost')
          );
        for (const [candidateIndex, candidate] of uniqueSelectedCandidates.entries()) {
          try {
            if (debugFailLastCandidate && candidateIndex === uniqueSelectedCandidates.length - 1) {
              throw { response: { status: 403, data: { message: 'REL-250 debug reject' } } };
            }

            const result = await openHHResumeContacts(candidate.id, {
              canViewContacts: candidate.canViewContacts,
              vacancyId: campaignEstaffVacancyId || undefined,
            });
            const resume = result.resume;
            const openedCandidate = pickOpenedCampaignCandidate(result);

            candidatesWithContacts.push({
              ...candidate,
              email: openedCandidate.email,
              phone: openedCandidate.phone,
              contactsOpened: true,
              contactsDisplay: CONTACTS_OPENED_LABEL,
              telegram: openedCandidate.telegram,
              whatsapp: openedCandidate.whatsapp,
              messengerLinks: openedCandidate.messengerLinks,
              photo: openedCandidate.photo,
              resumeUrl: openedCandidate.resumeUrl || candidate.resumeUrl,
              experience: resume.total_experience?.months ? `${resume.total_experience.months} months` : null,
              skills: resume.skill_set?.map(s => s.name || s).filter(Boolean) || [],
              salary: resume.salary?.amount,
              updated: resume.updated_at,
              firstName: openedCandidate.firstName || candidate.firstName,
              lastName: openedCandidate.lastName || candidate.lastName,
              middleName: openedCandidate.middleName || candidate.middleName,
              fullName: openedCandidate.fullName || candidate.fullName
            });
          } catch (error) {
            console.error(`Error opening contacts for candidate ${candidate.id}:`, error);
            rejectedCandidates.push({
              candidate,
              reason: getOpenContactsFailureReason(error),
            });
          }
        }

        toast.dismiss('opening-contacts');
        toast.success(`Контакты открыты для ${candidatesWithContacts.length} из ${selectedCandidates.length} кандидатов`);
      } else if (isAvito) {
        campaignAvitoVacancyId = normalizeOptionalId(fullVacancyData?.avitoVacancyId || selectedVacancy?.avitoVacancyId);
        if (!campaignAvitoVacancyId) {
          toast.error('К выбранной вакансии не привязана вакансия Avito. Сначала привяжите вакансию Avito в карточке вакансии.');
          return;
        }

        toast.loading('Открываем контакты Авито...', { id: 'opening-contacts' });
        const avitoOpenErrors = [];
        let avitoMissingChatIdCount = 0;

        for (const candidate of uniqueSelectedCandidates) {
          try {
            const result = await openAvitoResumeContacts(candidate.id);
            const openedCandidate = pickOpenedAvitoCandidate(result);
            if (!openedCandidate.avitoChatId) {
              avitoMissingChatIdCount += 1;
              continue;
            }

            candidatesWithContacts.push({
              ...candidate,
              email: openedCandidate.email,
              phone: openedCandidate.phone,
              avitoChatId: openedCandidate.avitoChatId,
              contactsOpened: true,
              contactsDisplay: CONTACTS_OPENED_LABEL,
              canViewContacts: true,
              resumeUrl: openedCandidate.resumeUrl || candidate.resumeUrl,
              firstName: openedCandidate.firstName || candidate.firstName,
              lastName: openedCandidate.lastName || candidate.lastName,
              middleName: openedCandidate.middleName || candidate.middleName,
              fullName: openedCandidate.fullName || candidate.fullName || candidate.name,
            });
          } catch (error) {
            console.error(`Error opening Avito contacts for candidate ${candidate.id}:`, error);
            avitoOpenErrors.push(error);
          }
        }

        toast.dismiss('opening-contacts');
        if (candidatesWithContacts.length === 0) {
          if (avitoOpenErrors.length > 0) {
            toast.error(getAvitoContactsOpenErrorMessage(avitoOpenErrors[0]));
          } else if (avitoMissingChatIdCount > 0) {
            toast.error('Авито открыл контакты, но не вернул chat_id. Отправка сообщения недоступна.');
          } else {
            toast.error('Не удалось открыть контакты Авито');
          }
          return;
        }

        if (avitoOpenErrors.length > 0 || avitoMissingChatIdCount > 0) {
          toast(`Контакты Авито открыты для ${candidatesWithContacts.length} из ${selectedCandidates.length} кандидатов`, { icon: '⚠️' });
        } else {
          toast.success(`Контакты Авито открыты для ${candidatesWithContacts.length} из ${selectedCandidates.length} кандидатов`);
        }
      } else {
        // For eStaff, contacts are already available
        campaignEstaffVacancyId = await ensureEstaffLink(selectedVacancyId);
        candidatesWithContacts.push(...uniqueSelectedCandidates);
      }

      // FR-3.8: Валидация контактов — пропускаем только полностью пустых кандидатов.
      // Телефон НЕ обязателен — основной канал мессенджер HH (TASKNEIROKLYUCH-530).
      const { valid: validCandidates, skipped } = filterCandidatesForMailing(candidatesWithContacts, isHH);
      if (skipped > 0) {
        rejectedCandidates.push(...candidatesWithContacts
          .filter(candidate => !validCandidates.includes(candidate))
          .map(candidate => ({
            candidate,
            reason: 'нет контактов для связи',
          })));
      }

      if (validCandidates.length === 0) {
        if (filters.source !== 'estaff') {
          setCheckResult({
            total: uniqueSelectedCandidates.length,
            available: 0,
            blocked: rejectedCandidates.map(({ candidate, reason }) => ({
              name: getCandidateModalName(candidate),
              reason,
            })),
          });
          setPendingCampaignData({ mode: 'creation-review', campaignData: null });
          setCheckResultsModalOpen(true);
          setCreatingCampaign(false);
          return;
        }

        const reasons = rejectedCandidates.length > 0
          ? ` Причины: ${summarizeRejectionReasons(rejectedCandidates)}.`
          : '';
        toast.error(`В рассылку не добавлен ни один кандидат из ${uniqueSelectedCandidates.length}.${reasons}`);
        return;
      }

      // Проверка доступности кандидатов перед рассылкой (TASKNEIROKLYUCH-434, REL-326).
      //
      // REL-326: проверка запускается для ВСЕХ источников. Раньше она была под
      // `filters.source === 'estaff'`, а для HH/Avito модалка показывала
      // «доступны N» = числу выбранных кандидатов — то есть цифра вообще не была
      // результатом проверки. Занятые в E-Staff, кандидаты с активным диалогом и
      // уже откликнувшиеся на вакансию HH отваливались в SKIPPED / «Уже откликался»
      // уже после старта рассылки.
      let availableCandidates = dedupeCandidatesForCampaign(validCandidates);
      let checkBlocked = [];
      if (!skipCandidateCheck) {
      toast.loading('Проверяем доступность кандидатов...', { id: 'checking-candidates' });
      try {
        // Контакты берём из того же source-маппера, что и payload рассылки: диалоги
        // на стороне ai-recruiter ищутся по точному совпадению telegram/whatsapp/max/
        // avitoChatId/hhResumeId, поэтому проверять надо ровно те значения, которые
        // потом окажутся в кампании.
        const checkCandidatesSource = availableCandidates;
        const candidatesForCheck = transformCandidatesForAPI(checkCandidatesSource, filters.source || 'hh')
          .map((c, index) => {
            // E-Staff маппер не кладёт email в payload рассылки, а проверка занятости
            // в E-Staff идёт по phone+email — берём недостающее из исходного кандидата.
            const raw = checkCandidatesSource[index] || {};
            return {
              externalId: c.externalId || null,
              name: c.name || getCandidateModalName(raw),
              phone: c.messengerContacts?.phone || raw.phone || null,
              email: c.email || c.messengerContacts?.email || raw.email || null,
              telegram: c.messengerContacts?.telegram || null,
              whatsapp: c.messengerContacts?.whatsapp || null,
              max: c.messengerContacts?.max || null,
              avitoChatId: c.messengerContacts?.avitoChatId || null,
              hhResumeId: c.messengerContacts?.hhResumeId || null,
            };
          });
        const companyId = useAuthStore.getState().user?.companyId || null;
        const result = await checkCampaignCandidates(
          candidatesForCheck,
          companyId,
          selectedVacancyId,
          { hhVacancyId: campaignHhVacancyId || null },
        );
        toast.dismiss('checking-candidates');

        if (result.blocked && result.blocked.length > 0) {
          // TASKNEIROKLYUCH-434: исключаем blocked из validCandidates.
          // Матчинг по externalId, фолбэк — композитный ключ hhResumeId|phone|email:
          // он устойчив к E-Staff кандидатам без hhResumeId (добавленным по контактам).
          availableCandidates = filterBlockedCandidates(availableCandidates, result);
          checkBlocked = result.blocked;

          if (filters.source === 'estaff') {
            setCheckResult(result);
            setPendingCampaignData({ mode: 'estaff-precheck', filteredCandidates: availableCandidates, fullVacancyData, isHH });
            setCheckResultsModalOpen(true);
            setCreatingCampaign(false);
            return;
          }
          // HH/Avito: отдельную E-Staff модалку в этих потоках не показываем —
          // блокировки уходят в общий список «Не добавлено» модалки подтверждения.
        }
        // Все доступны — продолжаем создание
      } catch (err) {
        toast.dismiss('checking-candidates');
        // Ошибка проверки — предупреждаем, но не блокируем
        toast('Не удалось проверить доступность кандидатов. Рассылка будет создана без проверки.', { icon: '⚠️' });
      }
      } // skipCandidateCheck

      // Get vacancy title from context or prop
      const vacancyTitleToUse = fullVacancyData?.title || vacancyTitle || 'Вакансия';

      // Extract employment contract types from vacancy
      const employmentContractTypes = fullVacancyData?.employmentContractTypes || [];
      const hasTkRf = employmentContractTypes.includes('tk_rf');
      const hasIp = employmentContractTypes.includes('ip');
      const hasAccreditedIT = employmentContractTypes.includes('accredited_it');

      // Parse salary (remove spaces if it's a string)
      const parseSalary = (value) => {
        if (!value) return null;
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          const parsed = parseInt(value.replace(/\s/g, ''));
          return isNaN(parsed) ? null : parsed;
        }
        return null;
      };

      const salaryMax = parseSalary(fullVacancyData?.salaryTo);
      const salaryMin = parseSalary(fullVacancyData?.salaryFrom);

      // Get social package text (benefits)
      const benefits = fullVacancyData?.socialPackageText || '';
      const enabledMessengers = isHH && campaignHhVacancyId
        ? ['HH']
        : (isAvito ? ['AVITO'] : []);
      const defaultAccountSettings = {
        telegramAccountId: null,
        whatsappAccountId: null,
        maxAccountId: null,
        messengerPriority: enabledMessengers,
        enabledMessengers
      };
      const accountSettings = sanitizeInheritedAccountSettings(
        inheritedCampaignSettings.accountSettings || defaultAccountSettings,
        { allowHH: Boolean(campaignHhVacancyId) }
      );
      if (enabledMessengers.length > 0) {
        accountSettings.enabledMessengers = Array.from(new Set([
          ...(accountSettings.enabledMessengers || []),
          ...enabledMessengers,
        ]));
        accountSettings.messengerPriority = Array.from(new Set([
          ...(accountSettings.messengerPriority || []),
          ...enabledMessengers,
        ]));
      }

      const campaignData = {
        positionName: vacancyTitleToUse,
        targetPosition: vacancyTitleToUse,
        description: fullVacancyData?.description || '',
        requirements: fullVacancyData?.description || '',
        benefits: benefits,
        companyDescription: fullVacancyData?.companyDescription || '',
        teamDescription: '',
        salaryRange: (salaryMax || salaryMin) ? {
          min: salaryMin,
          max: salaryMax,
          currency: fullVacancyData?.salaryCurrency || filters?.salaryCurrency || 'RUR',
          isGross: false,
          showSalary: fullVacancyData?.salarySpecified ?? true
        } : null,
        jobDescription: {
          responsibilities: fullVacancyData?.description?.split('\n')[0] || '',
          requirements: fullVacancyData?.description || '',
          conditions: `${fullVacancyData?.workFormat || 'Офис'}, ${fullVacancyData?.workSchedule || 'Полный день'}`,
          benefits: benefits
        },
        workFormat: fullVacancyData?.workFormat || 'OFFICE',
        workSchedule: fullVacancyData?.workSchedule || 'FULL_DAY',
        location: {
          city: filters.cities?.[0] || null,
          isRelocationAvailable: !!fullVacancyData?.relocationLocation,
          relocationCities: fullVacancyData?.relocationLocation ? [fullVacancyData.relocationLocation] : []
        },
        employmentType: 'OFFICIAL',
        employmentTkRf: hasTkRf,
        employmentGph: true,
        employmentIp: hasIp,
        employmentSelfEmployed: true,
        isAccreditedIT: hasAccreditedIT,
        hasNDA: fullVacancyData?.hasNda ?? false,
        hasBonuses: fullVacancyData?.hasBonuses ?? false,
        candidateSource: filters.source === 'hh' ? 'HH.RU' : (isAvito ? 'AVITO' : 'ESTAFF'),
        relocationCountry: fullVacancyData?.relocationLocation || null,
        relocationSupport: fullVacancyData?.relocationAssistance || null,
        recruiterName: '',
        recruiterId: useAuthStore.getState().user?.id ? String(useAuthStore.getState().user.id) : undefined,
        responseDelayMin: 30,
        responseDelayMax: 60,
        aiScreeningEnabled: fullVacancyData?.aiScreeningEnabled ?? selectedVacancy?.aiScreeningEnabled ?? false,
        hrInterviewEnabled: fullVacancyData?.hrInterviewEnabled ?? selectedVacancy?.hrInterviewEnabled ?? true,
        domain: window.location.hostname,
        accountSettings,
        searchFilters: filters,
        // Legacy fields for backward compatibility
        socialPackage: {
          min: salaryMin,
          max: salaryMax,
          currency: fullVacancyData?.salaryCurrency || filters?.salaryCurrency || 'RUR',
          isGross: false,
          showSalary: fullVacancyData?.salarySpecified ?? true
        },
        salaryDisclosure: fullVacancyData?.salarySpecified ?? true,
        ndaText: fullVacancyData?.hasNda ? 'NDA required' : null,
        customPrompts: {
          hasBonuses: fullVacancyData?.hasBonuses ?? false
        },
        attempts: 3,
        maxActiveDialogs: 50,
        dailyLimit: 20,
        pauseBetweenAttemptsHours: 6,
        contactWindowStart: 10,
        contactWindowEnd: 22,
        ...inheritedCampaignSettings,
        accountSettings,
        status: 'DRAFT',
        positionId: selectedVacancyId || null,
        hhVacancyId: campaignHhVacancyId || undefined,
        avitoVacancyId: campaignAvitoVacancyId || undefined,
        estaffVacancyId: campaignEstaffVacancyId || undefined,
        // TASKNEIROKLYUCH-434: filteredCandidates=null означает «не проверяли» → все.
        // Пустой массив означает «все заблокированы», но до этой точки мы не доходим —
        // ранний return в начале handleCreateCampaign обрывает поток. Array.isArray-guard
        // защищает от регрессии, если кто-то изменит early-return логику.
        candidates: transformCandidatesForAPI(
          dedupeCandidatesForCampaign(Array.isArray(filteredCandidates) ? filteredCandidates : availableCandidates),
          filters.source || 'hh'
        )
      };

      if (!skipCandidateCheck && filters.source !== 'estaff') {
        setCheckResult({
          total: uniqueSelectedCandidates.length,
          available: campaignData.candidates.length,
          // REL-326: в «Не добавлено» попадают и те, кого отсеяла проверка доступности
          // (E-Staff, активный диалог/писали за 30 дней, отклик на вакансию HH), а не
          // только кандидаты без контактов.
          blocked: [
            ...rejectedCandidates.map(({ candidate, reason }) => ({
              name: getCandidateModalName(candidate),
              reason,
            })),
            ...checkBlocked.map((item) => ({
              name: item.name || 'Неизвестный',
              reason: item.reason,
              source: item.source,
            })),
          ],
        });
        setPendingCampaignData({ mode: 'creation-review', campaignData });
        setCheckResultsModalOpen(true);
        setCreatingCampaign(false);
        return;
      }

      await createPreparedCampaign(campaignData);
    } catch (error) {
      console.error('Error creating campaign:', error);
      console.error('Error response:', error.response);
      console.error('Error request:', error.config);

      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);
      }

      const errorMessage = typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : error.response?.data?.error?.message || error.response?.data?.message || error.message;

      toast.error('Ошибка создания рассылки: ' + errorMessage);
    } finally {
      setCreatingCampaign(false);
    }
  };

  // Панель массовых действий: монтируем на появлении первого выбранного кандидата,
  // размонтируем после exit-анимации. Класс анимации меняется только на переходах
  // hidden↔visible — при изменении количества выбранных enter-анимация не проигрывается.
  const [selectionBarMounted, setSelectionBarMounted] = useState(selected.length > 0);
  const [selectionBarAnim, setSelectionBarAnim] = useState(selected.length > 0 ? 'ai-fade-in' : '');
  const prevHasSelectionRef = useRef(selected.length > 0);

  useEffect(() => {
    const has = selected.length > 0;
    const prev = prevHasSelectionRef.current;
    prevHasSelectionRef.current = has;
    if (has && !prev) {
      setSelectionBarMounted(true);
      setSelectionBarAnim('ai-fade-in');
      return;
    }
    if (!has && prev) {
      setSelectionBarAnim('ai-fade-out');
      const t = setTimeout(() => {
        setSelectionBarMounted(false);
        setSelectionBarAnim('');
      }, 200);
      return () => clearTimeout(t);
    }
  }, [selected.length]);

  const selectionBar = selectionBarMounted ? (
    <div className={`px-6 py-3 border-b border-gray-200 flex items-center justify-between ${selectionBarAnim}`}>
      <div className="text-sm text-gray-600">
        {`Выбрано: ${selected.length}`}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleCreateCampaign()}
          className={`bg-[var(--brand-primary)] box-border content-stretch flex gap-[8px] items-center px-[32px] py-[14px] relative rounded-[12px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] shrink-0 text-white text-sm font-medium transition-colors h-11`}
          style={{ backgroundColor: colors.primary }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.primaryHover; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.primary; }}
          title="Добавить в рассылку"
        >
          <EnvelopeIcon className="h-5 w-5" />
          <span>Добавить в рассылку ({selected.length})</span>
        </button>
        <button
          onClick={() => setEmailDialogOpen(true)}
          className={`bg-gray-100 box-border content-stretch flex gap-[8px] items-center px-[32px] py-[14px] relative rounded-[12px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] shrink-0 text-gray-700 text-sm font-medium transition-colors h-11 hover:bg-gray-200`}
          title="Отправить письма"
        >
          <EnvelopeIcon className="h-5 w-5" />
          <span>Отправить письма</span>
        </button>
        <button
          onClick={() => { setSelected([]); setSelectedCandidatesMap({}); }}
          className="bg-gray-100 box-border content-stretch flex gap-[8px] items-center px-[32px] py-[14px] relative rounded-[12px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] shrink-0 text-gray-700 text-sm font-medium transition-colors h-11 hover:bg-gray-200"
          title="Отменить выбор"
        >
          <XMarkIcon className="h-5 w-5" />
          <span>Отменить выбор</span>
        </button>
      </div>
    </div>
  ) : null;

  // State for completion animation before showing results
  const [completionStep, setCompletionStep] = useState(0);
  const [showingCompletion, setShowingCompletion] = useState(false);
  const prevLoadingRef = useRef(loading);

  // When loading finishes for relevance search, show completion animation
  useEffect(() => {
    if (prevLoadingRef.current && !loading && selectedRelevance && data?.items?.length > 0) {
      // Loading just finished, start completion animation
      setShowingCompletion(true);
      setCompletionStep(1);

      const timer1 = setTimeout(() => setCompletionStep(2), 300);
      const timer2 = setTimeout(() => setCompletionStep(3), 600);
      const timer3 = setTimeout(() => {
        setShowingCompletion(false);
        setCompletionStep(0);
      }, 1900);

      prevLoadingRef.current = loading;
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
    prevLoadingRef.current = loading;
  }, [loading, selectedRelevance, data?.items?.length]);

  // Reset completion state when starting new search
  useEffect(() => {
    if (loading) {
      setShowingCompletion(false);
      setCompletionStep(0);
    }
  }, [loading]);

  // Completion animation UI
  const CompletionLoader = () => (
    <div className="flex flex-col bg-white rounded-b-xl overflow-hidden min-h-[400px]">
      {selectionBar}
      <div className="w-full px-6 py-10">
        <div className="max-w-3xl mx-auto bg-white rounded-xl p-8 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-8">
            {/* Animated loader - now showing success */}
            <div className="flex-shrink-0">
              <div className="relative w-20 h-20">
                <div className="absolute inset-0 rounded-full border-4 border-green-100"></div>
                <div className="absolute inset-2.5 rounded-full bg-green-50 flex items-center justify-center">
                  <div className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Анализ завершён
              </h3>
              <p className="text-sm text-gray-500 mb-3">
                Кандидаты отсортированы по релевантности.
              </p>

              {scoringStats.total > 0 && (
                <div className="flex items-center gap-4 mb-4 text-xs">
                  <span className="text-gray-500">Проанализировано: <span className="font-medium text-gray-700">{scoringStats.total}</span></span>
                  {scoringStats.fromCache > 0 && (
                    <span className="text-green-600">Из кэша (бесплатно): <span className="font-medium">{scoringStats.fromCache}</span></span>
                  )}
                  {scoringStats.newlyScored > 0 && (
                    <span className="text-gray-500">Новых оценок: <span className="font-medium text-gray-700">{scoringStats.newlyScored} кр.</span></span>
                  )}
                </div>
              )}

              <div className="flex items-center gap-5 text-sm">
                <div className={`flex items-center gap-1.5 transition-colors duration-200 ${completionStep >= 1 ? 'text-green-600' : 'text-gray-400'}`}>
                  {completionStep >= 1 ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300"></div>
                  )}
                  <span>Поиск резюме</span>
                </div>

                <div className={`flex items-center gap-1.5 transition-colors duration-200 ${completionStep >= 2 ? 'text-green-600' : 'text-gray-400'}`}>
                  {completionStep >= 2 ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300"></div>
                  )}
                  <span>Оценка релевантности</span>
                </div>

                <div className={`flex items-center gap-1.5 transition-colors duration-200 ${completionStep >= 3 ? 'text-green-600' : 'text-gray-400'}`}>
                  {completionStep >= 3 ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300"></div>
                  )}
                  <span>Сортировка</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Show completion animation
  if (showingCompletion && selectedRelevance) {
    return <CompletionLoader />;
  }

  if (loading && !data?.items?.length) {
    // Special loader for relevance-filtered search (только когда нет данных)
    if (selectedRelevance) {
      // Calculate progress info from WebSocket data
      const progressText = searchProgress && searchProgress.status === 'processing'
        ? `Найдено ${searchProgress.collected} из ${searchProgress.target}`
        : 'Ищем подходящих кандидатов...';

      const progressPercentage = searchProgress?.percentage || 0;

      return (
        <div className="flex flex-col bg-white rounded-b-xl overflow-hidden min-h-[400px]">
          {selectionBar}
          <div className="w-full px-6 py-10">
            {/* Card container */}
            <div className="max-w-3xl mx-auto bg-white rounded-xl p-8 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-8">
                {/* Animated loader with progress */}
                <div className="flex-shrink-0">
                  <div className="relative w-20 h-20">
                    {/* Outer spinning ring */}
                    <div className="absolute inset-0 rounded-full border-4 border-gray-100"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-transparent animate-spin" style={{ borderTopColor: colors.primary }}></div>
                    {/* Inner circle with progress number or AI icon */}
                    <div className="absolute inset-2.5 rounded-full bg-gray-50 flex items-center justify-center">
                      {searchProgress && searchProgress.status === 'processing' ? (
                        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: colors.primary }}>
                          <span className="text-white text-sm font-bold">{searchProgress.collected}</span>
                        </div>
                      ) : (
                        <div className="w-9 h-9 rounded-full animate-pulse flex items-center justify-center" style={{ backgroundColor: colors.primary }}>
                          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Анализируем кандидатов с помощью ИИ
                  </h3>
                  <p className="text-sm text-gray-500 mb-2">
                    {progressText}
                  </p>

                  {/* Progress bar (only show when we have progress data) */}
                  {searchProgress && searchProgress.status === 'processing' && (
                    <div className="mb-4">
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-300 ease-out"
                          style={{
                            width: `${progressPercentage}%`,
                            backgroundColor: colors.primary
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-4 mt-2 flex-wrap">
                        <p className="text-sm text-gray-700">
                          Подошло: <span className="font-semibold text-gray-900">{searchProgress.collected}</span>
                          <span className="text-gray-400"> / {searchProgress.target}</span>
                        </p>
                        <span className="text-gray-300">·</span>
                        <p className="text-sm text-gray-700">
                          Проанализировано: <span className="font-semibold text-gray-900">{searchProgress.processed}</span>
                        </p>
                        <span className="text-gray-300">·</span>
                        <p className="text-sm text-gray-500">
                          Списано: <span className="font-medium text-gray-700">{searchProgress.newlyScored || 0}</span> кр.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Progress steps */}
                  <div className="flex items-center gap-5 text-sm">
                    <div className="flex items-center gap-1.5 text-green-600">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Поиск резюме</span>
                    </div>

                    <div className="flex items-center gap-1.5 font-medium" style={{ color: colors.primary }}>
                      <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: colors.primary, borderTopColor: 'transparent' }}></div>
                      <span>Оценка релевантности</span>
                    </div>

                    <div className="flex items-center gap-1.5 text-gray-400">
                      <div className="w-4 h-4 rounded-full border-2 border-gray-300"></div>
                      <span>Сортировка</span>
                    </div>
                  </div>

                  {/* Кнопка остановки оценки — цвет брендирования (colors.primary),
                      форма и тени по паттерну primary-кнопок проекта. */}
                  {onCancelSearch && (
                    <div className="mt-5">
                      <button
                        type="button"
                        onClick={onCancelSearch}
                        title="Остановить и показать что уже нашли"
                        className="box-border content-stretch flex gap-[8px] items-center px-[20px] py-[10px] relative rounded-[12px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] shrink-0 text-white text-sm font-medium transition-opacity hover:opacity-90"
                        style={{ backgroundColor: colors.primary }}
                      >
                        <XMarkIcon className="h-4 w-4" />
                        <span>Остановить поиск</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Если нет данных — показываем спиннер на весь экран
    if (!data?.items?.length) {
      return (
        <div className="flex flex-col bg-white rounded-b-xl overflow-hidden flex-1">
          {selectionBar}
          <div className="flex items-center justify-center flex-1">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderBottomColor: colors.primary }}></div>
              <p className="mt-2 text-gray-600">Загрузка...</p>
            </div>
          </div>
        </div>
      );
    }
    // Есть данные — показываем их с overlay загрузки (не стираем)
  }

  if (error && !data?.items?.length) {
    const errorMessage = getResultsLoadErrorMessage(error);

    return (
      <div className="flex flex-col bg-white rounded-b-xl overflow-hidden flex-1">
        {selectionBar}
        <div className="flex items-center justify-center flex-1">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 max-w-md">
            <p className="text-red-800">
              Ошибка загрузки данных: {errorMessage}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Флаги для overlay/banner поверх данных
  const showLoadingOverlay = loading && data?.items?.length > 0;
  const showErrorBanner = error && data?.items?.length > 0;

  const rawItems = data?.items || [];

  // Filter items by relevance if selectedRelevance is set
  // When using search-with-relevance endpoint, filtering is done server-side
  // Server returns only matching candidates, no client-side filtering needed
  // Check if data comes from server-side relevance search (has scoringApplied flag)
  const isServerFiltered = data?.scoringApplied === true;

  // Only apply client-side filter if server didn't filter (fallback for regular search)
  const items = (selectedRelevance && !isServerFiltered) ? rawItems.filter(item => {
    const scoreData = getDisplayScore(item.id, item);
    if (!scoreData) return false;

    const score = scoreData.score;
    switch (selectedRelevance) {
      case 'high':   return score >= 85;
      case 'medium': return score >= 70 && score < 85;
      case 'low':    return score < 70;
      default:       return true;
    }
  }) : rawItems;

  // Use server count when available (for proper pagination)
  const total = data?.estimatedFilteredTotal || data?.count || items.length;
  const totalFound = data?.totalFound; // Total in HH before relevance filtering
  const filteredCount = data?.filteredCount; // Passed relevance filter
  const isRelevanceFiltered = data?.scoringApplied && totalFound !== undefined;
  const isScoringDisabled = !scoringEnabled || (data?.scoringUnavailable && data?.reason === 'SCORING_DISABLED');
  const isCreditLimitHit = (data?.hardStop && data?.reason === 'CREDIT_LIMIT') || (data?.scoringUnavailable && data?.reason === 'CREDIT_LIMIT');
  const scoringLimitMessage = data?.scoringErrorMessage || 'Количество доступных оценок резюме исчерпано. Запросите расширение доступа у администратора.';
  const isApproximate = data?.isApproximate; // Число результатов приблизительное (фильтр джамперов)
  const isAvitoSource = filters.source === 'avito';
  const showStatusDateColumn = filters.source === 'estaff';
  const hasBoundedApproximatePagination = isAvitoSource && Number.isFinite(Number(data?.totalPages));
  const hhExhausted = data?.hhExhausted; // HH-страницы закончились
  const paginationMeta = data?.pagination || null;
  const isCursorPagination =
    paginationMeta?.mode === 'cursor' ||
    (!hasBoundedApproximatePagination && data?.hhCursor !== undefined && (data?.isApproximate || data?.hasNextPage !== undefined));
  const hasNextPage = typeof paginationMeta?.hasNextPage === 'boolean'
    ? paginationMeta.hasNextPage
    : (typeof data?.hasNextPage === 'boolean'
        ? data.hasNextPage
        : (isCursorPagination ? !hhExhausted : false));
  // TASKNEIROKLYUCH-645: rowsPerPage управляется ТОЛЬКО «N резюме»
  // (filters.limit). НЕЛЬЗЯ падать в fallback на data.pageSize: в relevance-
  // режиме handleSearch перезаписывает pageSize запроса на relevancePageSize
  // (target count), и бэк эхо-возвращает его же. Старый fallback протекал
  // target count в rowsPerPage — «Количество кандидатов» визуально меняло
  // размер страницы таблицы, чего быть не должно.
  const rowsPerPage = filters.limit || 20;

  // В режиме relevance-фильтра бэк возвращает ВСЕ отобранные кандидаты
  // одним ответом (pages=1), поэтому пагинация чисто клиентская.
  // state объявлен выше — здесь только вычисления.
  const serverPage = (filters.page || 1) - 1;
  const page = isRelevanceFiltered ? clientPage : serverPage;
  const serverTotalPages = Number.isFinite(Number(data?.totalPages)) && Number(data?.totalPages) > 0
    ? Number(data.totalPages)
    : null;
  const loadedTotalPages = Math.max(1, Math.ceil(items.length / rowsPerPage));
  const offsetTotalPages = serverTotalPages || Math.ceil(total / rowsPerPage);
  const cursorTotalPages = isRelevanceFiltered
    ? loadedTotalPages + (hasNextPage ? 1 : 0)
    : Math.max(serverPage + 1, hasNextPage ? serverPage + 2 : serverPage + 1);
  const totalPages = isCursorPagination
    ? cursorTotalPages
    : (isRelevanceFiltered ? offsetTotalPages : offsetTotalPages);
  const numberedTotalPages = isCursorPagination && isRelevanceFiltered
    ? loadedTotalPages
    : totalPages;
  const showNumberedPagination =
    (isCursorPagination || !isApproximate || hasBoundedApproximatePagination) &&
    numberedTotalPages > 1;
  // Если rowsPerPage уменьшили и текущий clientPage оказался за пределами —
  // отрендерим последнюю доступную (но setState не трогаем, чтобы не было
  // лишнего ререндера в рендер-фазе).
  const maxLoadedRelevancePage = isCursorPagination
    ? Math.max(0, loadedTotalPages - 1)
    : Math.max(0, totalPages - 1);
  const effectivePage = isRelevanceFiltered
    ? Math.min(page, maxLoadedRelevancePage)
    : page;

  // В relevance-режиме бэк возвращает все отфильтрованные айтемы разом —
  // нарезаем сами. В обычном режиме бэк уже отдал ровно одну страницу.
  const displayItems = isRelevanceFiltered
    ? items.slice(effectivePage * rowsPerPage, (effectivePage + 1) * rowsPerPage)
    : items;
  const displayedFrom = displayItems.length > 0 ? effectivePage * rowsPerPage + 1 : 0;
  const displayedTo = isRelevanceFiltered
    ? Math.min((effectivePage + 1) * rowsPerPage, items.length)
    : (isApproximate && hasBoundedApproximatePagination
        ? Math.min(effectivePage * rowsPerPage + displayItems.length, total)
        : Math.min((effectivePage + 1) * rowsPerPage, total));

  const handleLocalPageChange = (newPage, rowsPerPageArg) => {
    if (isRelevanceFiltered) {
      const targetPage = Math.max(0, Math.min(newPage, Math.max(0, totalPages - 1)));
      const needsServerPage = isCursorPagination && ((targetPage + 1) * rowsPerPageArg > items.length);
      if (needsServerPage && hasNextPage && onPageChange) {
        pendingClientPageRef.current = targetPage;
        onPageChange(targetPage, rowsPerPageArg);
        return;
      }
      const maxLocalPage = isCursorPagination
        ? Math.max(0, loadedTotalPages - 1)
        : Math.max(0, totalPages - 1);
      setClientPage(Math.max(0, Math.min(targetPage, maxLocalPage)));
    } else if (onPageChange) {
      const shouldClamp = !isCursorPagination && (!isApproximate || hasBoundedApproximatePagination);
      const targetPage = shouldClamp
        ? Math.max(0, Math.min(newPage, Math.max(0, totalPages - 1)))
        : newPage;
      onPageChange(targetPage, rowsPerPageArg);
    }
  };
  const shouldShowPagination = isCursorPagination
    ? (effectivePage > 0 || hasNextPage || totalPages > 1)
    : totalPages > 1;

  const selectedSet = new Set(selected);
  const areAllPageItemsSelected = items.length > 0 && items.every(item => selectedSet.has(item.id));

  return (
    <div className="relative flex flex-col bg-white rounded-b-xl overflow-hidden">
      {/* Overlay загрузки поверх существующих данных */}
      {showLoadingOverlay && (
        <div className="absolute inset-0 bg-white/60 z-20 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderBottomColor: colors.primary }}></div>
            <p className="mt-2 text-gray-600 text-sm">Загрузка...</p>
          </div>
        </div>
      )}

      {/* Баннер ошибки поверх данных */}
      {showErrorBanner && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 bg-red-50 border border-red-200 rounded-lg px-4 py-2 shadow-sm max-w-lg">
          <p className="text-red-800 text-sm">
            Ошибка загрузки: {getResultsLoadErrorMessage(error)}
          </p>
        </div>
      )}

      {/* Top actions bar (visible only when candidates are selected) */}
      {selectionBar}

      {/* Table */}
       <div className="overflow-x-auto overflow-y-auto">
         <table className="w-full divide-y divide-gray-200 min-w-full" style={{ tableLayout: 'auto' }}>
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
                                             <th className="px-5 pb-2 pt-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={areAllPageItemsSelected}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                  style={{ accentColor: colors.primary }}
                />
              </th>
                <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[240px]">
                  <div className="leading-tight">ФИО</div>
              </th>
                <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="leading-tight">Контакты</div>
              </th>
                <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[280px]">
                  <div className="leading-tight">Комментарий</div>
              </th>
                <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[300px]">
                  <div className="leading-tight">Должность</div>
              </th>
                <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="leading-tight">Город</div>
              </th>
                <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="leading-tight">Зарплата</div>
              </th>
                <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="leading-tight">Возраст</div>
              </th>
                <th className="px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px] min-w-[150px]">
                  <div className="leading-tight">Статус</div>
              </th>
              {showStatusDateColumn && (
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[130px] min-w-[130px]">
                  <div className="leading-tight">Дата статуса</div>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayItems.map((row) => {
              const rowOverride = aiRowOverrides[row.id] || null;
              const effectiveRow = {
                ...row,
                ...(isCreditLimitHit ? {
                  scoringError: row.scoringError || 'CREDIT_LIMIT',
                  scoringErrorMessage: row.scoringErrorMessage || scoringLimitMessage
                } : {}),
                ...(rowOverride || {})
              };
              const statusDateText = formatStatusDate(getStatusDateValue(effectiveRow));
              const effectiveClientComment = aiComments[row.id];
              const effectiveClientRating = aiRatings[row.id];
              const commentCardText = effectiveRow.scoringError
                ? 'Ошибка оценки резюме'
                : getRatingLabel(effectiveClientComment !== undefined ? effectiveClientRating : effectiveRow.aiRating);
              const commentCardBody = effectiveRow.scoringError
                ? null
                : (effectiveClientComment !== undefined ? effectiveClientComment : effectiveRow.aiComment);
              const hasCommentCard = Boolean(
                effectiveRow.scoringError ||
                (effectiveClientComment && effectiveClientComment !== '-') ||
                (effectiveRow.aiRating !== undefined && effectiveRow.aiRating > 0)
              );
              const canExpandCommentCard = Boolean(effectiveRow.scoringError || commentCardBody);
              const isSelected = selected.includes(row.id);
              const isHH = filters.source === 'hh';
              const isAvito = filters.source === 'avito';
              const isClicked = clickedRowId === row.id;
              const candidatePhotoUrl = isHH ? null : row.photoUrl;
              const hhResumeUrl = isHH ? pickSafeHHResumeUrl(row) : null;
              
              // Handle row click for highlighting
              const handleRowClick = (e) => {
                // Don't highlight if clicking on interactive elements
                if (e.target.closest('input, button, a, [role="button"]')) {
                  return;
                }
                saveClickedRow(row.id);
              };

              
              return (
                <tr
                  key={row.id}
                  onClick={handleRowClick}
                  className={`
                    cursor-pointer transition-all duration-200 ease-in-out
                    ${isClicked ? 'border-l-[3px] shadow-sm' : 'border-l-[3px] border-transparent'}
                    ${isSelected ? 'bg-primary-50' : ''}
                    ${!isClicked && !isSelected ? 'hover:bg-[#FAFBFC]' : ''}
                  `}
                  style={isClicked ? {
                    backgroundColor: `${colors.primary}08`,
                    borderLeftColor: colors.primary
                  } : undefined}
                >
                  <td className="px-5 pb-2 pt-3 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelect(row)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                      style={{ accentColor: colors.primary }}
                    />
                  </td>
                   <td className="px-1 py-2 align-middle">
                    <div className="flex items-center">
                      {candidatePhotoUrl ? (
                        <img
                          src={candidatePhotoUrl}
                          alt="Фото"
                          className="h-6 w-6 rounded-full mr-2 flex-shrink-0 bg-gray-200"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`h-6 w-6 rounded-full mr-2 flex-shrink-0 bg-gray-200 flex items-center justify-center ${candidatePhotoUrl ? 'hidden' : ''}`}>
                        <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                      </div>
                       <div className="min-w-0 flex-1">
                        {(() => {
                          const nameText = isHH && isTitleOnlyHHName(row)
                            ? getSafeText(row.title || row.position || row.displayName)
                            : isHH
                              ? formatCandidateShortName({
                                  firstName: contactsData[row.id]?.firstName || row.firstName || row.first_name,
                                  first_name: contactsData[row.id]?.firstName || row.first_name,
                                  lastName: contactsData[row.id]?.lastName || row.lastName || row.last_name,
                                  last_name: contactsData[row.id]?.lastName || row.last_name,
                                  middleName: contactsData[row.id]?.middleName || row.middleName || row.middle_name,
                                  middle_name: contactsData[row.id]?.middleName || row.middle_name,
                                  fullName: contactsData[row.id]?.name || row.fullName,
                                  name: row.name,
                                  displayName: row.displayName,
                                }, row.displayName || 'Кандидат')
                            : isAvito
                              ? getAvitoCandidateDisplayName(row, contactsData[row.id])
                              : (row.fullName || row.name || '—');
                          const displayName = String(nameText);
                          const nameClassName = 'text-sm font-medium text-gray-900 leading-tight whitespace-normal break-words max-w-[240px]';
                          if (hhResumeUrl) {
                            return (
                              <a
                                href={hhResumeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="candidate-name-hh-link"
                                className={`${nameClassName} block hover:text-primary-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-sm`}
                                title="Открыть резюме на HH.ru"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  saveClickedRow(row.id);
                                }}
                              >
                                {displayName}
                              </a>
                            );
                          }

                          return (
                            <div className={nameClassName}>
                              {displayName}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </td>
                   <td className="px-3 py-2 align-top">
                    {(() => {
                      const hhRawContactsOpened = Boolean(
                        contactsData[row.id]?.phone ||
                        row.phone ||
                        contactsData[row.id]?.email ||
                        row.email ||
                        contactsData[row.id]?.telegram ||
                        row.telegram ||
                        contactsData[row.id]?.whatsapp ||
                        row.whatsapp ||
                        contactsData[row.id]?.messengerLinks?.telegram ||
                        row.messengerLinks?.telegram ||
                        contactsData[row.id]?.messengerLinks?.whatsapp ||
                        row.messengerLinks?.whatsapp
                      );
                      const phone = isHH ? null : (contactsData[row.id]?.phone || row.phone);
                      const email = isHH ? null : (contactsData[row.id]?.email || row.email);
                      const avitoChatId = isAvito ? (contactsData[row.id]?.avitoChatId || row.avitoChatId) : null;
                      const hhContactsOpened = Boolean(
                        contactsData[row.id]?.contactsOpened ||
                        row.contactsOpened ||
                        contactsData[row.id]?.contactsDisplay ||
                        row.contactsDisplay ||
                        hhRawContactsOpened
                      );
                      const avitoContactsOpened = Boolean(
                        avitoChatId ||
                        contactsData[row.id]?.contactsOpened ||
                        row.contactsOpened ||
                        contactsData[row.id]?.contactsDisplay ||
                        row.contactsDisplay
                      );
                      const hasContacts = isHH ? hhContactsOpened : (isAvito ? avitoContactsOpened : (phone || email));
                      const hhTelegramValue = isHH && hhContactsOpened
                        ? (
                          contactsData[row.id]?.messengerLinks?.telegram ||
                          row.messengerLinks?.telegram ||
                          contactsData[row.id]?.telegram ||
                          row.telegram
                        )
                        : null;
                      const hhPhoneValue = isHH && hhContactsOpened
                        ? (contactsData[row.id]?.phone || row.phone)
                        : null;
                      const hhWhatsAppValue = isHH && hhContactsOpened
                        ? (
                          contactsData[row.id]?.messengerLinks?.whatsapp ||
                          row.messengerLinks?.whatsapp ||
                          contactsData[row.id]?.whatsapp ||
                          row.whatsapp ||
                          hhPhoneValue
                        )
                        : null;
                      const telegramLink = phone
                        ? getTelegramLink(phone)
                        : getTelegramLink(hhTelegramValue);
                      const whatsAppLink = phone
                        ? getWhatsAppLink(phone)
                        : getWhatsAppLink(hhWhatsAppValue);
                      const resumeUrl = isHH ? hhResumeUrl : (contactsData[row.id]?.resumeUrl || row.resumeUrl);

                      return (
                        <div className="space-y-1.5">
                          {/* Контакты в плашке */}
                          {hasContacts && isHH && (
                            <div className="rounded-lg px-2.5 py-1.5" style={{ backgroundColor: '#F6F8FA' }}>
                              <div className="flex items-center gap-1 text-[11px] font-medium text-gray-600">
                                <span>{contactsData[row.id]?.contactsDisplay || row.contactsDisplay || CONTACTS_OPENED_LABEL}</span>
                              </div>
                            </div>
                          )}
                          {hasContacts && isAvito && (
                            <div className="rounded-lg px-2.5 py-1.5" style={{ backgroundColor: '#F6F8FA' }}>
                              <div className="flex items-center gap-1 text-[11px] font-medium text-gray-600">
                                <span>{contactsData[row.id]?.contactsDisplay || row.contactsDisplay || CONTACTS_OPENED_LABEL}</span>
                              </div>
                            </div>
                          )}
                          {hasContacts && !isHH && !isAvito && (
                            <div className="rounded-lg px-2.5 py-1.5" style={{ backgroundColor: '#F6F8FA' }}>
                              <div className="flex items-center gap-3 text-[11px] text-gray-600">
                                {phone && (
                                  <div
                                    className="flex items-center gap-1 min-w-0 cursor-pointer hover:text-gray-900 transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(phone);
                                      toast.success('Телефон скопирован', { duration: 2000 });
                                    }}
                                    title="Скопировать телефон"
                                  >
                                    <svg className="h-3 w-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    <span className="truncate">{phone}</span>
                                  </div>
                                )}
                                {phone && email && <span className="text-gray-300">|</span>}
                                {email && (
                                  <div
                                    className="flex items-center gap-1 min-w-0 cursor-pointer hover:text-gray-900 transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(email);
                                      toast.success('Email скопирован', { duration: 2000 });
                                    }}
                                    title="Скопировать email"
                                  >
                                    <svg className="h-3 w-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    <span className="truncate max-w-[120px]" title={email}>{email}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Действия */}
                          <div className="flex items-center gap-1.5">
                            {telegramLink && (
                              <a
                                href={telegramLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:text-blue-700 p-0.5"
                                title="Написать в Telegram"
                                onClick={(e) => { e.stopPropagation(); saveClickedRow(row.id); }}
                              >
                                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
                                </svg>
                              </a>
                            )}
                            {whatsAppLink && (
                              <a
                                href={whatsAppLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-600 hover:text-green-700 p-0.5"
                                title="Написать в WhatsApp"
                                onClick={(e) => { e.stopPropagation(); saveClickedRow(row.id); }}
                              >
                                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                </svg>
                              </a>
                            )}
                            {resumeUrl && (
                              <a
                                href={resumeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => { e.stopPropagation(); saveClickedRow(row.id); }}
                                className="text-primary-600 hover:text-primary-800 p-0.5"
                                title={isAvito ? 'Открыть на Авито' : 'Открыть на HH.ru'}
                              >
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </a>
                            )}
                            {isHH ? (
                              <button
                                data-testid="candidate-resume-preview-button"
                                aria-label="Просмотр резюме"
                                onClick={(e) => { e.stopPropagation(); saveClickedRow(row.id); handleShowHHResume(row); }}
                                className="text-primary-600 hover:text-primary-800 p-0.5"
                                title="Просмотр резюме"
                              >
                                <EyeIcon className="h-3.5 w-3.5" />
                              </button>
                            ) : isAvito ? (
                              <button
                                data-testid="candidate-resume-preview-button"
                                aria-label="Просмотр резюме"
                                onClick={(e) => { e.stopPropagation(); saveClickedRow(row.id); handleShowAvitoResume(row); }}
                                className="text-primary-600 hover:text-primary-800 p-0.5"
                                title="Просмотр резюме"
                              >
                                <EyeIcon className="h-3.5 w-3.5" />
                              </button>
                            ) : (
                              <button
                                data-testid="candidate-resume-preview-button"
                                aria-label="Просмотр резюме"
                                onClick={(e) => { e.stopPropagation(); saveClickedRow(row.id); handleShowEStaffResume(row); }}
                                className="text-primary-600 hover:text-primary-800 p-0.5"
                                title="Просмотр резюме"
                              >
                                <EyeIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {((isHH && !contactsData[row.id] && !row.email && !row.phone) || (isAvito && !avitoChatId)) && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleShowContacts(row); }}
                                disabled={loadingContacts[row.id]}
                                className="text-primary-600 hover:text-primary-800 p-0.5"
                                title="Открыть контакты"
                              >
                                {loadingContacts[row.id] ? (
                                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2" style={{ borderBottomColor: 'var(--brand-primary)' }}></div>
                                ) : (
                                  <UserIcon className="h-3.5 w-3.5" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                   <td className="px-3 py-2 min-w-[280px]">
                    {isScoringDisabled ? (
                      <div className="text-xs text-gray-400 text-center py-2">
                        —
                      </div>
                    ) : loadingComments[row.id] ? (
                      <div className="flex items-center justify-center py-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderBottomColor: 'var(--brand-primary)' }}></div>
                      </div>
                    ) : queuedComments[row.id] ? (
                      <div className="flex items-center justify-center py-2">
                        <div className="flex items-center gap-2 text-gray-400">
                          <svg className="h-4 w-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                          </svg>
                          <span className="text-xs">В очереди...</span>
                        </div>
                      </div>
                    ) : hasCommentCard ? (() => {
                      const displayRating = effectiveClientComment !== undefined ? effectiveClientRating : effectiveRow.aiRating;
                      const comment = commentCardBody;
                      const popoverContent = effectiveRow.scoringError
                        ? { type: 'error', message: effectiveRow.scoringErrorMessage || 'Оценка резюме временно недоступна' }
                        : (comment && typeof comment === 'object')
                          ? {
                              type: 'facts',
                              positiveFacts: Array.isArray(comment.positiveFacts) ? comment.positiveFacts : [],
                              negativeFacts: Array.isArray(comment.negativeFacts) ? comment.negativeFacts : [],
                            }
                          : { type: 'text', text: String(comment || '') };
                      const popoverHeader = {
                        rating: effectiveRow.scoringError ? null : displayRating,
                        label: commentCardText,
                      };
                      const isOpen = aiPopover?.rowId === row.id;
                      return (
                        <button
                          type="button"
                          className={`rounded-lg p-2 w-full text-left bg-[#F6F8FA] transition-all duration-200 ${
                            canExpandCommentCard
                              ? 'cursor-pointer hover:bg-[#ECEFF3] hover:shadow-sm'
                              : 'cursor-default'
                          } ${isOpen ? 'ring-2 ring-blue-300' : ''}`}
                          disabled={!canExpandCommentCard}
                          onClick={(e) => {
                            if (!canExpandCommentCard) return;
                            e.stopPropagation();
                            const anchorRect = e.currentTarget.getBoundingClientRect();
                            if (isOpen) {
                              setAiPopover(null);
                            } else {
                              setAiPopover({
                                rowId: row.id,
                                anchorRect,
                                header: popoverHeader,
                                content: popoverContent,
                              });
                            }
                          }}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {!effectiveRow.scoringError && displayRating !== undefined && displayRating > 0 && (
                              <span
                                className="inline-flex items-center justify-center px-1.5 py-0.5 rounded text-sm font-medium shrink-0"
                                style={{
                                  backgroundColor: getRatingBadgeStyle(displayRating).bg,
                                  color: getRatingBadgeStyle(displayRating).text
                                }}
                              >
                                {displayRating}%
                              </span>
                            )}
                            <span className="text-xs font-medium text-black truncate">
                              {commentCardText}
                            </span>
                            {!effectiveRow.scoringError && effectiveRow.fromCache && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-400 shrink-0" title={effectiveRow.scoredAt ? `Оценено: ${format(new Date(effectiveRow.scoredAt), 'd MMM yyyy', { locale: ru })}` : 'Из кэша'}>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {effectiveRow.scoredAt ? format(new Date(effectiveRow.scoredAt), 'd MMM', { locale: ru }) : 'кэш'}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })() : row.matchComment && !isScoringDisabled && !effectiveRow.scoringError && effectiveRow.aiRating !== null ? (
                      <div className="rounded-lg p-2" style={{ backgroundColor: '#F6F8FA' }}>
                        <p className="text-xs text-gray-600 leading-relaxed">{row.matchComment}</p>
                      </div>
                    ) : (
                      <div className="text-xs text-gray-400 text-center py-2">
                        —
                      </div>
                    )}
                   </td>
                   <td className="px-5 py-2 align-middle min-w-[300px]">
                    <div className="text-sm text-gray-900 leading-tight whitespace-normal break-words">
                       {getSafeText(row.title || row.position)}
                     </div>
                   </td>
                  <td className="px-5 py-2">
                     <div className="text-sm text-gray-900">
                       {(() => {
                         const raw = row.location || row.area?.name || row.city || '—';
                         const text = typeof raw === 'string' ? raw : String(raw || '—');
                         return text.length > 12 ? `${text.slice(0, 10)}...` : text;
                       })()}
                     </div>
                   </td>
                   <td className="px-5 py-2">
                     <div className="text-sm text-gray-900 truncate">
                       {formatSalary(row.salary)}
                     </div>
                   </td>
                   <td className="px-5 py-2 text-center">
                     <div className="text-sm text-gray-900 truncate">
                       {getSafeText(row.age)}
                     </div>
                   </td>
                   <td className="pr-2 py-2 align-middle w-[150px] min-w-[150px]">
                    <div className="max-w-[150px] whitespace-normal break-words">
                      {isHH ? (
                        <span className={`${
                          row.jobSearchStatus === 'active_search' ? 'bg-green-100 text-green-800' :
                          row.jobSearchStatus === 'looking_for_offers' ? 'bg-yellow-100 text-yellow-800' :
                          row.jobSearchStatus === 'has_job_offer' ? 'text-white' :
                          row.jobSearchStatus === 'accepted_job_offer' ? 'bg-purple-100 text-purple-800' :
                          row.jobSearchStatus === 'not_looking_for_job' ? 'bg-gray-100 text-gray-800' :
                          'bg-blue-100 text-blue-800'
                        } inline-block max-w-full pr-[5px] pl-[5px] py-0.5 text-xs font-semibold rounded-md whitespace-normal break-words`}
                          style={row.jobSearchStatus === 'has_job_offer' ? { backgroundColor: colors.primary } : undefined}
                        >
                          {getSafeText(row.jobSearchStatusText) || (
                            row.jobSearchStatus === 'active_search' ? 'Активный' :
                            row.jobSearchStatus === 'looking_for_offers' ? 'Рассматривает' :
                            row.jobSearchStatus === 'not_looking_for_job' ? 'Не ищет' :
                            'HH.ru'
                          )}
                        </span>
                      ) : (
                        <span className={`${getEstaffStatusClasses(effectiveRow.currentStatus)} inline-block max-w-full pr-[5px] pl-[5px] py-0.5 text-xs font-semibold rounded-md whitespace-normal break-words`}>
                          {/* TASKNEIROKLYUCH-673: fallback '—' (не «Новый»). Раньше при отсутствии
                              данных все плашки врали «Новый», что было исходным багом задачи. */}
                          {getSafeText(effectiveRow.currentStatus?.name, '—')}
                        </span>
                      )}
                    </div>
                  </td>
                  {showStatusDateColumn && (
                    <td className="px-3 py-2 align-middle w-[130px] min-w-[130px]">
                      <span className="text-sm text-gray-700 whitespace-nowrap">
                        {statusDateText}
                      </span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {scoringStats.total > 0 && !isScoringDisabled && (
        <div className="px-6 py-2 border-t border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>Проанализировано: <span className="font-medium text-gray-700">{scoringStats.total}</span></span>
            {scoringStats.fromCache > 0 && (
              <span className="text-green-600">Из кэша (бесплатно): <span className="font-medium">{scoringStats.fromCache}</span></span>
            )}
            {scoringStats.newlyScored > 0 && (
              <span>Новых оценок: <span className="font-medium text-gray-700">{scoringStats.newlyScored} кр.</span></span>
            )}
          </div>
        </div>
      )}

      {/* TASKNEIROKLYUCH-645: единая пагинация для обоих режимов.
          В обычном режиме — серверная (handlePageChange → re-fetch).
          В relevance-режиме — клиентская (setClientPage, без запросов). */}
      <div className="px-6 py-4 border-t border-gray-200">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-gray-700">
            {isRelevanceFiltered ? (
              <>
                {isApproximate ? (
                  <>Всего ~{totalFound?.toLocaleString()}, подходящих {filteredCount} (число приблизительное)</>
                ) : (
                  <>Всего {totalFound?.toLocaleString()}, подходящих {filteredCount}</>
                )}
                {totalPages > 0 && (
                  <span className="text-gray-500 ml-2">
                    · Показано {displayedFrom} - {displayedTo} из {items.length}
                  </span>
                )}
              </>
            ) : isApproximate && !hasBoundedApproximatePagination ? (
              <>
                Страница {effectivePage + 1}
                {data?.jumpersFiltered > 0 && (
                  <span className="text-gray-400 ml-2">
                    (найдено ~{total?.toLocaleString()}, джамперов отфильтровано: {data.jumpersFiltered})
                  </span>
                )}
              </>
            ) : isApproximate && hasBoundedApproximatePagination ? (
              <>Показано {displayedFrom} - {displayedTo} из ~{total}</>
            ) : (
              <>Показано {displayedFrom} - {displayedTo} из {total}</>
            )}
          </div>

          {shouldShowPagination && (
            <div className="flex items-center gap-2">
              <button
                aria-label="Предыдущая страница"
                onClick={() => handleLocalPageChange(effectivePage - 1, rowsPerPage)}
                disabled={effectivePage === 0}
                className="p-2 rounded-3xl border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>

              {showNumberedPagination && (
                <div className="flex items-center gap-1">
                  {(() => {
                    const maxButtons = Math.min(5, numberedTotalPages);
                    const startPage = Math.max(0, Math.min(effectivePage - Math.floor(maxButtons / 2), numberedTotalPages - maxButtons));
                    return [...Array(maxButtons)].map((_, i) => {
                      const pageNum = startPage + i;
                      const isActive = pageNum === effectivePage;
                      return (
                        <button
                          key={pageNum}
                          aria-label={`Страница ${pageNum + 1}`}
                          onClick={() => handleLocalPageChange(pageNum, rowsPerPage)}
                          className={`px-3 py-1 rounded-xl ${
                            isActive
                              ? 'text-white'
                              : 'hover:bg-gray-50'
                          }`}
                          style={isActive ? { backgroundColor: colors.primary } : undefined}
                        >
                          {pageNum + 1}
                        </button>
                      );
                    });
                  })()}
                </div>
              )}

              {isApproximate && !isRelevanceFiltered && !hasBoundedApproximatePagination && !showNumberedPagination && (
                <span className="text-sm text-gray-500 px-2">Стр. {effectivePage + 1}</span>
              )}

              <button
                aria-label="Следующая страница"
                onClick={() => handleLocalPageChange(effectivePage + 1, rowsPerPage)}
                disabled={
                  isCursorPagination
                    ? (!hasNextPage && effectivePage >= loadedTotalPages - 1)
                    : isRelevanceFiltered
                      ? effectivePage >= totalPages - 1
                      : hasBoundedApproximatePagination
                        ? effectivePage >= totalPages - 1
                        : isApproximate
                          ? (hhExhausted && items.length < rowsPerPage)
                          : (effectivePage >= totalPages - 1)
                }
                className="p-2 rounded-3xl border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>

      <EmailDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        selectedCandidates={items.filter(item => selected.includes(item.id))}
        sourcePositionId={sourcePositionId}
        vacancyTitle={vacancyTitle}
      />

      <ContactsDialog
        open={contactsDialogOpen}
        onClose={() => {
          setContactsDialogOpen(false);
          setSelectedResumeId(null);
          setSelectedResumeData(null);
          setContactEstaffVacancyId('');
          setEstaffLinkMissing(false);
          setCheckingContactEstaffLink(false);
        }}
        onConfirm={handleConfirmShowContacts}
        loading={Boolean((selectedResumeId && loadingContacts[selectedResumeId]) || checkingContactEstaffLink)}
        estaffLinkMissing={estaffLinkMissing}
        sourceLabel={filters.source === 'avito' ? 'Авито' : 'HH.ru'}
      />

      <HHResumeModal
        open={hhResumeModalOpen}
        onClose={() => {
          setHhResumeModalOpen(false);
          setSelectedHHResume(null);
        }}
        resumeData={selectedHHResume}
        searchKeywords={Array.from(new Set([
          ...((Array.isArray(filters.skillGroups)
            ? filters.skillGroups.flatMap(g => Array.isArray(g?.skills) ? g.skills : [])
            : []) || []),
          ...(filters.skillsAll || []),
          ...(filters.skillsAny || []),
          filters.positionName,
          filters.expectedPositionName,
          ...(filters.donorCompanies || [])
        ].map(v => (v === null || v === undefined) ? '' : String(v).trim()).filter(Boolean)))}
        searchCities={[
          ...(filters.cities || []),
          ...(filters.cityIds || [])
        ].filter(Boolean)}
      />

      <EStaffResumeModal
        open={estaffResumeModalOpen}
        onClose={() => {
          setEstaffResumeModalOpen(false);
          setSelectedEStaffResume(null);
        }}
        resumeData={selectedEStaffResume}
        searchKeywords={Array.from(new Set([
          ...((Array.isArray(filters.skillGroups)
            ? filters.skillGroups.flatMap(g => Array.isArray(g?.skills) ? g.skills : [])
            : []) || []),
          ...(filters.skillsAll || []),
          ...(filters.skillsAny || []),
          filters.positionName,
          filters.expectedPositionName,
          ...(filters.donorCompanies || [])
        ].map(v => (v === null || v === undefined) ? '' : String(v).trim()).filter(Boolean)))}
        searchCities={[
          ...(filters.cities || []),
          ...(filters.cityIds || [])
        ].filter(Boolean)}
      />

      <AvitoResumeModal
        open={avitoResumeModalOpen}
        onClose={() => {
          setAvitoResumeModalOpen(false);
          setSelectedAvitoResume(null);
        }}
        resumeData={selectedAvitoResume}
        searchKeywords={Array.from(new Set([
          ...((Array.isArray(filters.skillGroups)
            ? filters.skillGroups.flatMap(g => Array.isArray(g?.skills) ? g.skills : [])
            : []) || []),
          ...(filters.skillsAll || []),
          ...(filters.skillsAny || []),
          filters.positionName,
          filters.expectedPositionName,
          ...(filters.donorCompanies || [])
        ].map(v => (v === null || v === undefined) ? '' : String(v).trim()).filter(Boolean)))}
        searchCities={[
          ...(filters.cities || []),
          ...(filters.cityIds || [])
        ].filter(Boolean)}
      />

      <CampaignCheckResultsModal
        open={checkResultsModalOpen}
        onClose={() => {
          setCheckResultsModalOpen(false);
          setCheckResult(null);
          setPendingCampaignData(null);
        }}
        onConfirm={() => {
          if (pendingCampaignData?.mode === 'creation-review') {
            if (pendingCampaignData.campaignData) {
              setCheckResultsModalOpen(false);
              createPreparedCampaign(pendingCampaignData.campaignData);
            }
            return;
          }

          // Продолжить создание рассылки — пропустить повторную проверку
          if (pendingCampaignData?.mode === 'estaff-precheck') {
            setCheckResultsModalOpen(false);
            handleCreateCampaign({ skipCandidateCheck: true, filteredCandidates: pendingCampaignData.filteredCandidates });
          }
        }}
        loading={creatingCampaign}
        checkResult={checkResult}
        blockedTitle={pendingCampaignData?.mode === 'creation-review' ? 'Не добавлено:' : undefined}
      />

      {estaffLinkGuardModal}

      {aiPopover && ReactDOM.createPortal(
        <AiCommentPopover
          anchorRect={aiPopover.anchorRect}
          header={aiPopover.header}
          content={aiPopover.content}
          onClose={() => setAiPopover(null)}
        />,
        document.body
      )}
    </div>
  );
};

// Popover с AI-анализом кандидата. Открывается справа от плашки с процентом,
// при нехватке места уходит влево. Закрывается по клику вне, Esc и при любом
// скролле — чтобы не отрываться от привязки к ячейке.
const AI_POPOVER_WIDTH = 380;
const AI_POPOVER_MARGIN = 8;

function AiCommentPopover({ anchorRect, header, content, onClose }) {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState({
    top: anchorRect.top,
    left: anchorRect.right + AI_POPOVER_MARGIN,
    placement: 'right',
  });

  useLayoutEffect(() => {
    const pop = popoverRef.current;
    if (!pop) return;
    const popHeight = pop.offsetHeight;
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;

    let left = anchorRect.right + AI_POPOVER_MARGIN;
    let placement = 'right';
    if (
      left + AI_POPOVER_WIDTH > viewportW - 8 &&
      anchorRect.left - AI_POPOVER_MARGIN - AI_POPOVER_WIDTH > 8
    ) {
      left = anchorRect.left - AI_POPOVER_MARGIN - AI_POPOVER_WIDTH;
      placement = 'left';
    }

    let top = anchorRect.top;
    if (top + popHeight > viewportH - 8) {
      top = Math.max(8, viewportH - popHeight - 8);
    }

    setPosition({ top, left, placement });
  }, [anchorRect]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onScroll = () => onClose();
    const onResize = () => onClose();
    const onOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) onClose();
    };
    // Отложенная регистрация mousedown: открывающий клик иначе сразу же
    // пометится как «клик вне» и popover закроется в тот же кадр.
    const id = window.setTimeout(() => {
      document.addEventListener('mousedown', onOutside);
    }, 0);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', onOutside);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [onClose]);

  const ratingStyle = header.rating != null && header.rating > 0 ? getRatingBadgeStyle(header.rating) : null;

  return (
    <div
      ref={popoverRef}
      className="fixed z-[9999] bg-white rounded-xl shadow-2xl border border-gray-200 p-4 overflow-y-auto overscroll-contain"
      style={{
        top: position.top,
        left: position.left,
        width: AI_POPOVER_WIDTH,
        maxHeight: 'calc(100vh - 16px)',
        animation: `aiPopoverIn${position.placement === 'left' ? 'Left' : 'Right'} 180ms ease-out`,
      }}
    >
      <style>{`
        @keyframes aiPopoverInRight {
          from { opacity: 0; transform: translateX(-4px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes aiPopoverInLeft {
          from { opacity: 0; transform: translateX(4px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
        {ratingStyle && (
          <span
            className="inline-flex items-center justify-center px-2 py-0.5 rounded text-base font-semibold"
            style={{ backgroundColor: ratingStyle.bg, color: ratingStyle.text }}
          >
            {header.rating}%
          </span>
        )}
        <span className="text-sm font-medium text-[#0A0D14] truncate">{header.label}</span>
      </div>

      {content.type === 'error' && (
        <p className="text-xs text-gray-600 leading-relaxed">{content.message}</p>
      )}

      {content.type === 'facts' && (
        <div className="space-y-2">
          {content.positiveFacts.length > 0 && (
            <ul className="space-y-1.5">
              {content.positiveFacts.map((fact, i) => (
                <li key={`pos-${i}`} className="flex items-start gap-2 text-xs text-green-700 leading-[16px]">
                  <img src={AiFactPositive} alt="" className="mt-0.5 h-5 w-5 flex-shrink-0" />
                  <span className="break-words">{fact}</span>
                </li>
              ))}
            </ul>
          )}
          {content.negativeFacts.length > 0 && (
            <ul className="space-y-1.5">
              {content.negativeFacts.map((fact, i) => (
                <li key={`neg-${i}`} className="flex items-start gap-2 text-xs text-red-700 leading-[16px]">
                  <img src={AiFactNegative} alt="" className="mt-0.5 h-5 w-5 flex-shrink-0" />
                  <span className="break-words">{fact}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {content.type === 'text' && (
        <p className="text-xs text-gray-600 leading-relaxed">{content.text}</p>
      )}
    </div>
  );
}

function getRatingBadgeStyle(rating) {
  if (typeof rating !== 'number') return { bg: '#E5E7EB', text: '#6B7280' };
  if (rating >= 75) return { bg: '#CBF5E5', text: '#176448' };
  if (rating >= 50) return { bg: '#FBDFB1', text: '#693D11' };
  return { bg: '#FECACA', text: '#991B1B' };
}

export default ResultsTable;
